/**
 * SessionVolumeMount — the unified viewport render (#575; default path).
 *
 * The ONE React mount for the hanging-protocol grid, rendering through the
 * viewport stack (ViewportWorkspace + ViewportSession + a RenderStrategy)
 * instead of the inline Cornerstone calls in VolumeViewport/StackViewport. The
 * `viewportKind` prop selects the strategy — GpuVolumeStrategy ("volume",
 * preferred) or Cpu2dStrategy ("stack", the weak-GPU / non-reconstructable
 * fallback) — and the navigation lifecycle (NavigationController +
 * SessionGeometry) is strategy-agnostic, so scroll/sign/AI-jump can't diverge
 * by render kind (the #570 drift this collapses). `?newViewport=0` is the soak
 * escape hatch back to the legacy components.
 *
 * Wired: render + scroll, window/level (VOI), invert, camera (zoom/pan) mirror,
 * segmentation labelmap + contour rep + drawing tools, AI-jump (world-space,
 * FoR-gated), pendingJump cross-series start, and the single anatomic-sign
 * source (onAnatomicSignChange on mount-ready + post-stream flip).
 *
 * NOT here (out of scope, see #575): MIP/MinIP blend (slice_dependent layout
 * mounts VolumeViewport directly via SliceDependentGrid), on-the-fly reslicing
 * for the stack strategy (it renders the reconstructed per-plane series as-is),
 * and runtime GpuVolume→Cpu2d downgrade on persistent GPU context loss.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAtomValue, useSetAtom, getDefaultStore } from "jotai";
import type { RenderingEngine } from "@cornerstonejs/core";
import {
  getCornerstone,
  getCornerstoneTools,
  initializeCornerstone,
} from "../../utils/dicomLoader";
import {
  SHARED_RENDERING_ENGINE_ID,
  getOrCreateSharedRenderingEngine,
} from "../../utils/renderingEngine";
import {
  configureToolGroup,
  ensureContourSegmentationActive,
  getContourSegmentationId,
  switchActiveTool,
} from "../../utils/viewportToolSetup";
import {
  activeSegmentIndexAtom,
  annotationStartTsAtom,
  bumpRadiusAtom,
  contourVersionAtom,
  derivedMeasurementsAtom,
  hasFirstStrokeAtom,
  isDrawingAtom,
  isSegmentationActiveAtom,
  lockedSeriesIdAtom,
  nnInteractiveLoadingAtom,
  segmentationVolumeIdAtom,
  seriesSegReadyFamily,
} from "../../atoms/segmentationAtoms";
import { activeMeasurementToolAtom } from "../../atoms/measurementAtoms";
import { viewerSurfacesAtom } from "../../atoms/viewerSurfacesAtom";
import { shouldCardinalSnapForSurfaces } from "../../viewport/cameraSnap";
import { measureModeAtom, measureSessionAtom } from "../../atoms/measureAtoms";
import { setBumpRadius as setBumpRadiusModule } from "../../tools/BumpContourTool";
import {
  setRegionGrowAutoSwitchCallback,
  setRegionGrowCommitCallback,
} from "../../tools/RegionGrowTool";
import { isLoadedFromMask } from "../../utils/annotationFlags";
import {
  buildS3ImageIds,
  registerImagePlaneMetadataForSeries,
  registerImagePixelMetadataForSeries,
  registerGeneralSeriesMetadataForSeries,
  registerVoiLutMetadataForSeries,
  type StudyMetadataResponse,
} from "../../utils/dicomMetadataLoader";
import { derivePlane } from "../../utils/seriesClassification";
import { paneNeedsRadiologicalFlip } from "../../viewport/radiologicalOrientation";
import { resolveMountPlane } from "./resolveMountPlane";
import { resolveStackStartIndex } from "../stackStartIndex";
import { aiJumpTargetAtom, type AiJumpTarget } from "../../atoms/positionAtoms";
import { kStepToListIndex } from "../volumeViewport.kStepToListIndex";
import { PLANE_COLORS_HEX } from "../../constants/planeColors";
import { SegmentationBadge } from "../SegmentationBadge";
import { logger } from "../../utils/logger";
import type { ViewportWorkspace } from "../../viewport/ViewportWorkspace";
import { useSegmentationCursor } from "../../hooks/useSegmentationCursor";
import { useSharedWorkspace } from "./ViewportWorkspaceContext";
import { ViewportSession } from "../../viewport/ViewportSession";
import { createStudyMetadataGeometry, type IppResolver } from "../../viewport/SessionGeometry";
import { createResliceGeometry } from "../../viewport/ResliceGeometry";
import { GpuVolumeStrategy } from "../../viewport/strategies/GpuVolumeStrategy";
import { prioritizeVolumeFrame } from "../../viewport/prioritizeVolumeFrame";
import { isProgressiveEnabled } from "@/utils/progressiveConfig";
import { Cpu2dStrategy } from "../../viewport/strategies/Cpu2dStrategy";
import type { RenderStrategy } from "../../viewport/strategies/RenderStrategy";
import type { RecoverableSession } from "../../viewport/ContextLossRecovery";
import type { PlaneOrientation } from "../../viewport/types";
import type { ViewportKind } from "../../utils/pickViewportComponent";
import { sliceWorldPositionFromMetadata } from "../volumeViewport.sliceWorldPosition";
import { resolveAiJumpWorld } from "../aiJumpWorld";
import { pushUndo } from "../../tools/annotationUndo";
import { isLassoContourEdit } from "../../tools/SegmentationLassoTool";

// Canonical Cornerstone CORE event name (NOT the CORNERSTONE_TOOLS_* tools
// prefix — mixing them silently no-ops listeners). Defined once here so the
// add/remove sites can't drift. Verified against VolumeViewport.tsx:548-551.
const VOLUME_LOADING_COMPLETED_EVENT = "CORNERSTONE_IMAGE_VOLUME_LOADING_COMPLETED";
// Stack-path landed-slice event. StackViewport fires this with a list-index
// `imageIdIndex` directly (no K-step reorder — setStack uses the imageIds list
// order verbatim), so the stack listener bridges it without kStepToListIndex.
// Verified against StackViewport.tsx:234.
const STACK_NEW_IMAGE_EVENT = "CORNERSTONE_STACK_NEW_IMAGE";

// ─── AI-jump FoR gate + log helpers — copied VERBATIM from VolumeViewport.tsx
//     (103-132) so the warn ids match across BOTH paths (ops can correlate FoR
//     bails regardless of which viewport path is live). ───────────────────────
type AiJumpGateResult = { ok: true } | { ok: false; reason: "for_bail_null" | "for_bail_mismatch" };

function checkAiJumpForGate(
  ai: AiJumpTarget,
  seriesId: string,
  ownFor: string | null,
): AiJumpGateResult {
  if (ai.seriesId === seriesId) return { ok: true };
  if (!ai.frameOfReferenceUid || !ownFor) return { ok: false, reason: "for_bail_null" };
  if (ai.frameOfReferenceUid !== ownFor) return { ok: false, reason: "for_bail_mismatch" };
  return { ok: true };
}

function logAiJumpBail(
  reason: "for_bail_null" | "for_bail_mismatch",
  ctx: "effect" | "bind",
  ai: AiJumpTarget,
  seriesId: string,
  ownFor: string | null,
): void {
  logger.warn(
    "ai_slice_jump_cross %s seriesId=%s targetSeriesId=%s ownFor=%s targetFor=%s ctx=%s",
    reason,
    seriesId,
    ai.seriesId,
    ownFor ?? "null",
    ai.frameOfReferenceUid ?? "null",
    ctx,
  );
}

interface SessionVolumeMountProps {
  seriesId: string;
  studyMetadata: StudyMetadataResponse;
  studyId: string;
  /** Render strategy selector (from `decideViewport(meta, gpuVerdict)` —
   *  "volume" preferred, "stack" fallback). "volume" → GpuVolumeStrategy
   *  (GPU volume actor); "stack" → Cpu2dStrategy (StackViewport over the
   *  series imageIds). The single navigation lifecycle (NavigationController +
   *  SessionGeometry) is strategy-agnostic, so scroll/sign/AI-jump can't
   *  diverge between the two. Defaults to "volume". */
  viewportKind?: ViewportKind;
  /** Build the per-series labelmap + register a segmentation rep so the
   *  drawing tools work. Volume path: always (region-grow is safe on a real
   *  volume actor). Stack path: only the weak-GPU fallback on a
   *  geometrically-sound series — NOT blocker / is_reconstructable=false
   *  series, where the source volume can't be built and region-grow is unsafe
   *  (mirrors StackViewport's enableSegmentation gate). Defaults to true. */
  enableSegmentation?: boolean;
  /** When set, this pane is an MPR reslice of `seriesId`'s shared volume: the
   *  viewport is oriented to this plane and the geometry uses it. No effect on
   *  the rendered series data (same cached volume). */
  reslicePlane?: PlaneOrientation;
  /** Stable viewport id for this slot — same value VolumeViewport receives
   *  (`hangingProtocol_${slotId}`), so overlays that look the viewport up by
   *  that id/prefix still find the new-path viewport. */
  viewportIdOverride?: string;
  /** External slice control — when provided, the mount navigates here. */
  externalSliceIndex?: number;
  /** The slot's load-time cross-series jump target (a finding-jump slice),
   *  distinct from the current slice. On the STACK path it picks the initial
   *  stack index (resolveStackStartIndex — middle default, jump-target override),
   *  mirroring legacy StackViewport so a finding-jump to a not-yet-displayed
   *  series lands on the finding's slice. On the VOLUME path the volume-ready
   *  gate in ProtocolViewport promotes it into sliceIndex (→ externalSliceIndex),
   *  so it's not consumed here. (#571 model.) */
  pendingJump?: number | null;
  /** When no externalSliceIndex is given, open on the middle slice instead of
   *  edge slice 0 (coronal/sagittal panes — avoids the black edge slice).
   *  Mirrors VolumeViewport. */
  startAtMiddleSlice?: boolean;
  /** Window center (VOI) — applied to the viewport after attach + on change.
   *  Mirrors VolumeViewport. */
  windowCenter?: number;
  /** Window width (VOI). Mirrors VolumeViewport. */
  windowWidth?: number;
  /** Mirror slot invert into Cornerstone. Mirrors VolumeViewport. */
  isInverted?: boolean;
  /** Fires (center, width) on every CORNERSTONE_VOI_MODIFIED (drag-windowing)
   *  so the slot windowing atom + the W:/L: overlay track the gesture. */
  onWindowChange?: (center: number, width: number) => void;
  /** Legacy zoom-only mirror. Mirrors VolumeViewport (kept for surface parity). */
  onZoomChange?: (zoom: number) => void;
  /** Fires (zoom, pan) on every CORNERSTONE_CAMERA_MODIFIED — the camera-state
   *  mirror feeding the slot (zoom,pan) atoms (overlay zoom readout + minimap). */
  onCameraChange?: (zoom: number, pan: [number, number]) => void;
  /** Fires the landed list-index back to the parent (ProtocolViewport bridges
   *  it into the slot `sliceIndex` atom so the slice-readout overlay updates). */
  onSliceChange?: (slice: number, total: number) => void;
  /** Fires the slot's anatomic-up sign (+1/-1 in list-index space) whenever the
   *  geometry resolves it — on mount-ready AND on the post-stream flip (once the
   *  real per-slice IPP streams and supersedes the synthesized eager-cache
   *  value). The parent bridges it into `slotStateFamily(slotId).listAnatomicSign`
   *  (read by AnatomicScrollTool / SliceNavigator / ↑↓). This is the SINGLE
   *  source of scroll direction for the unified path — fired by the mount's own
   *  readiness, NOT `volumeStatus`, so it lands for the stack strategy too
   *  (the #570 fix: the legacy effect's recompute was gated on a volume-only
   *  signal, so the corrected sign never reached the stack path). */
  onAnatomicSignChange?: (sign: 1 | -1) => void;
  /** Same signature as VolumeViewport's. This increment only emits a coarse
   *  "displayable" signal once the volume is attached (loaded === total === N)
   *  so the parent's overlay-gating (`isViewportDisplayable`) flips on and the
   *  slice-readout / orientation markers render. Per-frame streaming progress
   *  is NOT reported yet — TODO(A4-next). */
  onLoadingStateChange?: (
    isLoading: boolean,
    loadedFrames: number,
    totalFrames: number,
    isViewportDisplayable: boolean,
  ) => void;
  // NOTE: no `coordinatedResize` prop. Resize is coordinated centrally by
  // HangingProtocolGrid across all panes on the shared engine (keyed by
  // `hangingProtocol_${slotId}` + `engine.getViewports()`), which this mount
  // satisfies — so it adds NO local ResizeObserver (one here would re-introduce
  // the multi-pane camera-reset race). A standalone (non-grid) caller is not yet
  // supported; add the prop back when that path exists.
}

const SessionVolumeMount: React.FC<SessionVolumeMountProps> = ({
  seriesId,
  studyMetadata,
  studyId,
  viewportKind = "volume",
  enableSegmentation = true,
  reslicePlane,
  viewportIdOverride,
  externalSliceIndex,
  pendingJump,
  startAtMiddleSlice = false,
  windowCenter,
  windowWidth,
  isInverted,
  onWindowChange,
  onZoomChange,
  onCameraChange,
  onSliceChange,
  onAnatomicSignChange,
  onLoadingStateChange,
}) => {
  const elementRef = useRef<HTMLDivElement>(null);
  // The layout-scoped workspace published by HangingProtocolGrid. When present,
  // this pane joins it (addSession/removeSession) instead of creating its own,
  // so all panes share ONE engine and ONE set of context-loss listeners.
  // Mirrored into a ref so the mount IIFE + cleanup closure read the same value
  // the render captured (the effect is keyed on series/study, not the context).
  const sharedWorkspace = useSharedWorkspace();
  const sharedWorkspaceRef = useRef<ViewportWorkspace | null>(sharedWorkspace);
  sharedWorkspaceRef.current = sharedWorkspace;
  const sessionRef = useRef<ViewportSession | null>(null);
  // Strategy-agnostic: GpuVolumeStrategy on the volume path, Cpu2dStrategy on
  // the stack path. The camera/dispose/getCamera calls below are all on the
  // RenderStrategy interface, so neither callsite needs to know which is live.
  const strategyRef = useRef<RenderStrategy | null>(null);
  // Render kind, captured for the long-lived listeners + setProperties helper
  // so they branch volume-vs-stack without re-reading the prop (which could
  // change identity across renders).
  const viewportKindRef = useRef<ViewportKind>(viewportKind);
  viewportKindRef.current = viewportKind;
  // Landed list-index, tracked so the wheel handler can step relative to it
  // without reading React state (which would close over a stale value).
  const currentSliceRef = useRef<number>(0);
  const totalSlicesRef = useRef<number>(0);
  // imageIds list (list-index ordered) — read by the VOLUME_NEW_IMAGE landed-
  // index listener to translate Cornerstone's K-step → list-index (#401).
  const imageIdsRef = useRef<string[] | null>(null);
  // The landed-index listener. It is the AUTHORITY on the current slice after
  // ANY camera move — including external jumps (SegmentationAnnotationPanel
  // handleSelect's direct jumpToWorld, cross-plane jumps, reference-line drags)
  // that bypass session.setSlice. Without it the slice readout + currentSliceRef
  // go stale after a finding-jump. Mirrors VolumeViewport's
  // volumeNewImageListenerRef (§10 landed-index invariant). The EVENT differs by
  // strategy — VOLUME_NEW_IMAGE (capture, K-step) on the volume path,
  // STACK_NEW_IMAGE (capture, list-index) on the stack path — so the event name
  // is tracked alongside the handler for a matching removeEventListener.
  const landedImageListenerRef = useRef<((evt: any) => void) | null>(null);
  const landedImageEventRef = useRef<string | null>(null);
  // Capture changing callbacks so the long-lived wheel listener always reads
  // the latest without re-binding on every render.
  const onSliceChangeRef = useRef(onSliceChange);
  onSliceChangeRef.current = onSliceChange;
  // Cross-series jump target, mirrored into a ref so the (async) mount IIFE
  // reads the latest after attach resolves. SET_SERIES seeds it synchronously
  // with the series swap, so it's known by the time the mount runs (mirrors
  // StackViewport's pendingJumpRef).
  const pendingJumpRef = useRef(pendingJump);
  pendingJumpRef.current = pendingJump;
  // The slot sliceIndex, mirrored the same way. On a cross-series swap the
  // volume-ready gate can promote the jump target into it while attach is still
  // in flight (the pane was displayable on the previous series), when the
  // external-slice effect has no session to drive yet. The mount must land on
  // that latest value, not the 0 the swap reset it to when the mount started.
  const externalSliceIndexRef = useRef(externalSliceIndex);
  externalSliceIndexRef.current = externalSliceIndex;
  const onAnatomicSignChangeRef = useRef(onAnatomicSignChange);
  onAnatomicSignChangeRef.current = onAnatomicSignChange;
  const onLoadingStateChangeRef = useRef(onLoadingStateChange);
  onLoadingStateChangeRef.current = onLoadingStateChange;
  // W/L + camera callbacks/props mirrored into refs so the long-lived element
  // listeners (VOI_MODIFIED, CAMERA_MODIFIED) + the mount IIFE read the latest
  // values without re-binding. Mirrors VolumeViewport's propsRef pattern.
  const onWindowChangeRef = useRef(onWindowChange);
  onWindowChangeRef.current = onWindowChange;
  const onCameraChangeRef = useRef(onCameraChange);
  onCameraChangeRef.current = onCameraChange;
  const onZoomChangeRef = useRef(onZoomChange);
  onZoomChangeRef.current = onZoomChange;
  const windowCenterRef = useRef(windowCenter);
  windowCenterRef.current = windowCenter;
  const windowWidthRef = useRef(windowWidth);
  windowWidthRef.current = windowWidth;
  const isInvertedRef = useRef(isInverted);
  isInvertedRef.current = isInverted;
  // VOI mirror-out listener + the de-dupe guard that breaks the
  // drag→VOI_MODIFIED→onWindowChange→atom→prop→apply→VOI_MODIFIED feedback loop.
  // Load-bearing — relocated intact from VolumeViewport (615-626/1218-1245/955).
  const voiModifiedListenerRef = useRef<((evt: any) => void) | null>(null);
  const lastAppliedWindowRef = useRef<{ center: number; width: number } | null>(null);
  // Camera mirror-out listener (zoom/pan → onCameraChange + session snapshot).
  const cameraModifiedListenerRef = useRef<(() => void) | null>(null);
  // Cross-viewport AI jump (relocated from VolumeViewport 1877-1881). Per-trigger
  // cursor: left UNSET on a no-strategy bail so the mount-IIFE bind-site can pick
  // up a target that fired mid-attach (first-frame gate).
  const aiJumpTarget = useAtomValue(aiJumpTargetAtom);
  const lastFiredAiJumpTriggerRef = useRef<number | null>(null);
  // List-ordered imageIds of an AI target's OWN series, for resolving the jump
  // world-point against Cornerstone's real per-frame geometry (#921). The
  // pane's own list when the target is this pane's series (exact, however the
  // ids were built); the target series' canonical list for a same-FoR
  // cross-series pane. Empty when neither resolves — the caller falls back to
  // the sidecar ipp.
  const aiJumpImageIds = (ai: AiJumpTarget): string[] => {
    if (ai.seriesId === seriesId) return imageIdsRef.current ?? [];
    const cdn = studyMetadata?.canonical_cdn_base_url;
    const instances = studyMetadata?.series?.[ai.seriesId];
    return cdn && instances?.length ? buildS3ImageIds(cdn, instances) : [];
  };

  // ─── Segmentation gating (A4-seg-1; C2 single-owner) ──────────────────────
  // `segmentationVolumeIdAtom` is the SOLE gate that ungates
  // SegmentationAnnotationPanel (restore saved masks, purple/pink color-flip,
  // save→lock→bump). C2 moves OWNERSHIP of that gate OUT of every pane and into
  // a single layout-level owner in HangingProtocolGrid — this pane no longer
  // writes it. Instead, once this pane's labelmap + segmentation are registered
  // it publishes its labelmap id to `seriesSegReadyFamily(seriesId)`; the owner
  // reads that, deterministically picks the priority series' labelmap,
  // pre-initializes segment metadata, then sets the gate ONCE (kills the old
  // per-pane last-writer-wins race that broke saved-mask restore + draw
  // segmentIndex). This pane still READS the gate (below) to activate the
  // shared contour seg on its own viewport.
  const setSeriesSegReady = useSetAtom(seriesSegReadyFamily(seriesId));
  const segmentationVolumeId = useAtomValue(segmentationVolumeIdAtom);
  const activeSegmentIndex = useAtomValue(activeSegmentIndexAtom);

  // ─── Drawing / tool-group wiring (A4-seg-2) ───────────────────────────────
  // Relocated from VolumeViewport.tsx. The atom setters below feed the
  // region-grow commit callback (which enables Save + locks the series), and
  // activeMeasurementTool drives the switchActiveTool effect.
  const activeMeasurementTool = useAtomValue(activeMeasurementToolAtom);
  const setActiveMeasurementTool = useSetAtom(activeMeasurementToolAtom);
  const setIsDrawing = useSetAtom(isDrawingAtom);
  const setHasFirstStroke = useSetAtom(hasFirstStrokeAtom);
  const setLockedSeriesId = useSetAtom(lockedSeriesIdAtom);
  const setAnnotationStartTs = useSetAtom(annotationStartTsAtom);
  // Seg-lifecycle listener targets (A4-seg-3): the SEGMENTATION_DATA_MODIFIED
  // handler writes the HU/volume overlay; ANNOTATION_COMPLETED bumps the 3D
  // contour-refresh counter. Mirror VolumeViewport.tsx:350/354.
  const setDerivedMeasurements = useSetAtom(derivedMeasurementsAtom);
  const setContourVersion = useSetAtom(contourVersionAtom);
  // Measure-mode derivation, copied verbatim from VolumeViewport.tsx:1257-1260.
  // Measure mode is a radiology-viewer feature, not labeling, so on the
  // labeling flow these atoms stay at their defaults and `effectiveTool`
  // collapses to `activeMeasurementTool` — but we replicate the derivation
  // exactly to avoid any behavior divergence between the two paths.
  const measureMode = useAtomValue(measureModeAtom);
  const measureSession = useAtomValue(measureSessionAtom);
  const measureRefining = !!(measureMode && measureSession);
  const effectiveTool = measureMode && !measureRefining ? "measure" : activeMeasurementTool;
  // Ref-backed mirror of isSegmentationActive so the region-grow commit
  // callback (a long-lived closure) gates without re-binding (mirrors VV).
  const isSegmentationActive = useAtomValue(isSegmentationActiveAtom);
  const isSegmentationActiveRef = useRef(isSegmentationActive);
  useEffect(() => {
    isSegmentationActiveRef.current = isSegmentationActive;
  }, [isSegmentationActive]);

  // Ref-backed mirror of the active viewer surfaces, read at attach time (after
  // the volume-load await) to decide whether the post-attach camera snap runs.
  // Assigned during render (not via effect) so it's current even on a fast
  // (cached-volume) attach. On `labeling-seg` we keep the camera on the series'
  // true orientation so IPM-placed contours don't render across slices.
  const viewerSurfaces = useAtomValue(viewerSurfacesAtom);
  const viewerSurfacesRef = useRef(viewerSurfaces);
  viewerSurfacesRef.current = viewerSurfaces;

  // Stable per-mount viewportId, captured by the mount effect so the
  // tool-related effects below can address this slot's viewport on the shared
  // engine (mirrors VV's viewportIdRef).
  const viewportIdRef = useRef<string | null>(null);
  // The shared tool group this viewport joined — read by the switchActiveTool
  // effect (mirrors VV's toolGroupRef).
  const toolGroupRef = useRef<any>(null);

  const segmentationIdRef = useRef<string | null>(null);
  // The source (CT) volumeId for this mount — read by the
  // SEGMENTATION_DATA_MODIFIED listener (A4-seg-3) to fetch HU scalars for the
  // derived-measurement pass. Mirrors VolumeViewport.tsx:299 (volumeIdRef).
  const volumeIdRef = useRef<string | null>(null);
  // Cold-load retry: createAndCacheDerivedVolume needs real volume dimensions,
  // which aren't hydrated yet on first load. Stash the retry here for the
  // VOLUME_LOADING_COMPLETED listener to drain.
  const pendingSegSetupRef = useRef<(() => Promise<void>) | null>(null);
  // In-flight guard so the VOLUME_LOADING_COMPLETED listener can't run the
  // deferred retry concurrently (two completion events for the same volumeId
  // before the first resolves would otherwise double-register segmentations and
  // publish a stale readiness signal twice).
  const segSetupInFlightRef = useRef(false);
  const volumeLoadingCompletedListenerRef = useRef<((evt: any) => void) | null>(null);
  // Flips true once seg setup completes, gating the contour-rep effect below.
  const [segReady, setSegReady] = useState(false);
  // Flips true once the viewport has been enabled and joined the shared tool
  // group, gating the switchActiveTool effect below. Mirrors VV's
  // `lifecycleReady` (which gates the same effect there). Kept separate from
  // segReady because the tool group is wired before the async seg setup runs.
  const [toolsReady, setToolsReady] = useState(false);

  const [error, setError] = useState<string | null>(null);

  // ─── Mount / seriesId change — build the new-stack session and attach ─────
  useEffect(() => {
    let aborted = false;
    // Hoisted to the effect body (not the async IIFE) so the cleanup closure
    // can reference the same viewportId for seg-representation teardown.
    const viewportId = viewportIdOverride ?? "sessionVolumeMount_default";
    // Capture upfront so the tool-related effects (switchActiveTool,
    // region-grow callbacks) can address this slot's viewport, and so cleanup
    // uses the same value the async body did (mirrors VV:439).
    viewportIdRef.current = viewportId;
    // Tracks whether THIS mount added the viewport to the tool group, so
    // cleanup only removes a viewport it actually added (mirrors VV's
    // `toolGroupAdded`).
    let toolGroupAdded = false;

    (async () => {
      await initializeCornerstone();
      if (aborted) return;

      const element = elementRef.current;
      if (!element) return;

      // Cornerstone needs a non-zero viewport size before enable; a 0×0 WebGL
      // canvas never recovers. Same wait-for-size pattern as VolumeViewport.
      while (true) {
        const rect = element.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) break;
        await new Promise((r) => setTimeout(r, 50));
        if (aborted) return;
      }

      const instances = studyMetadata?.series?.[seriesId];
      if (!instances) {
        setError(`Series not available: ${seriesId}`);
        return;
      }
      if (!studyMetadata.canonical_cdn_base_url) {
        setError("No canonical CDN base URL configured");
        return;
      }

      // ── volumeId + imageIds derivation — reused verbatim from
      //    VolumeViewport.tsx (volumeId: line 853, imageIds: line 804, and the
      //    four metadata registrations: lines 841-852). makeVolumeMetadata
      //    reads imagePixel/generalSeries/imagePlane modules without
      //    null-checks, so they MUST be seeded before createAndCacheVolume.
      const seriesMeta = studyMetadata.series_metadata?.[seriesId];
      const imageIds = buildS3ImageIds(studyMetadata.canonical_cdn_base_url, instances);
      if (!imageIds.length) {
        setError("No images found in series");
        return;
      }
      // Expose to the VOLUME_NEW_IMAGE landed-index listener (kStep→list-index).
      imageIdsRef.current = imageIds;
      registerImagePlaneMetadataForSeries(
        studyMetadata.canonical_cdn_base_url,
        instances,
        seriesMeta,
      );
      registerImagePixelMetadataForSeries(studyMetadata.canonical_cdn_base_url, instances);
      registerGeneralSeriesMetadataForSeries(
        studyMetadata.canonical_cdn_base_url,
        instances,
        seriesMeta,
      );
      registerVoiLutMetadataForSeries(studyMetadata.canonical_cdn_base_url, instances, seriesMeta);
      const volumeId = `cornerstoneStreamingImageVolume:${studyId}_${seriesId}`;
      // Expose the source-volume id to the seg-lifecycle listeners (A4-seg-3),
      // which read volumeIdRef.current to fetch CT HU scalars for measurements.
      volumeIdRef.current = volumeId;

      // Plane drives the geometry's anatomicSign (which way a wheel-up step
      // moves in list-index space). The GPU strategy itself renders the
      // ACQUISITION plane regardless, so an imperfect plane only affects scroll
      // direction. derivePlane reads meta.plane / IOP / description; default to
      // Axial when none is derivable (matches GpuVolumeStrategy's ACQUISITION
      // default — ASSUMPTION noted in the A4 report).
      const plane: PlaneOrientation = resolveMountPlane(reslicePlane, seriesMeta);

      // #coronal-lr: the mount is the single owner of the radiological-L/R
      // decision — it is the only place that knows BOTH the series' stored IOP
      // and whether this pane is an MPR reslice (which renders on Cornerstone's
      // canonical patient-space camera and must never be flipped). Both
      // strategies receive it as a gate rather than re-deriving it, so the flip
      // and the orientation letters can't diverge on two IOP sources.
      const flipRadiologicalLR = paneNeedsRadiologicalFlip({
        nativeIop: seriesMeta?.image_orientation_patient ?? null,
        nativePlane: seriesMeta ? derivePlane(seriesMeta) : null,
        reslicePlane,
      });

      try {
        const cornerstone = getCornerstone();

        // Build the source volume in CPU cache when the render path needs a GPU
        // volume actor (GpuVolumeStrategy.attach → setVolumesForViewports) OR
        // when segmentation needs a labelmap backing (createAndCacheDerivedVolume
        // reads source-volume dims). The stack path does NOT need it to render
        // (Cpu2dStrategy.attach is setStack over imageIds), and on a genuinely
        // non-reconstructable series (blocker / is_reconstructable=false)
        // createAndCacheVolume THROWS on irregular spacing — the very reason the
        // series routes to stack — so building it there would regress a
        // currently-rendering pane to a black error pane. Reuse a sibling's
        // cached volume when present (avoids a double-fetch), else create + load.
        // Progressive: resolves when the full volume has decoded its first frame,
        // so the low-res preview stays up until swapping to full shows content.
        let fullFirstFrame: Promise<void> | null = null;
        const progressiveVolume = isProgressiveEnabled() && viewportKind === "volume";

        const needsSourceVolume = viewportKind === "volume" || enableSegmentation;
        if (needsSourceVolume) {
          let volume = cornerstone.cache.getVolume(volumeId);
          if (!volume) {
            try {
              volume = await cornerstone.volumeLoader.createAndCacheVolume(volumeId, { imageIds });
              if (aborted) return;
              if (progressiveVolume) {
                let firstFrameSeen = false;
                fullFirstFrame = new Promise<void>((resolve) => {
                  volume!.load(() => {
                    if (!firstFrameSeen) {
                      firstFrameSeen = true;
                      resolve();
                    }
                  });
                });
              } else {
                volume.load();
              }
            } catch (volErr) {
              if (aborted) return;
              // The volume path can't render without the actor — rethrow to the
              // outer catch (sets the error pane). On the stack path the volume
              // is only the seg labelmap backing; a build failure means seg is
              // unavailable but the stack still renders, so log + continue.
              if (viewportKind === "volume") throw volErr;
              logger.warn(
                "[SessionVolumeMount] source volume build failed on stack path — seg unavailable, rendering stack only. seriesId=%s err=%o",
                seriesId,
                volErr,
              );
            }
          }
        }
        if (aborted) return;

        // ── Build the new stack: Workspace → Session → GpuVolumeStrategy. The
        //    workspace owns engine lifecycle + context-loss recovery; the
        //    session owns navigation; the strategy owns the raw Cornerstone
        //    calls.
        //
        // A4 consolidation C1: use the LAYOUT-shared workspace published by
        // HangingProtocolGrid. It is created in the grid's lazy useState
        // initializer, so it is non-null in context on this pane's FIRST render
        // (no async race). All panes share ONE engine + ONE set of
        // webglcontextlost listeners; the grid owns the engine lifecycle and is
        // the ONLY place it's destroyed (on grid unmount). This pane must NEVER
        // create or mount its own workspace — doing so attaches a duplicate
        // listener set, the exact bug this consolidation removes. If the shared
        // workspace is somehow absent (flag on but Provider missing), fail loudly
        // rather than silently re-create that bug.
        const workspace = sharedWorkspaceRef.current;
        if (!workspace) {
          logger.error(
            "[SessionVolumeMount] no shared ViewportWorkspace in context — aborting new-path mount (flag on but Provider missing). viewportId=%s",
            viewportId,
          );
          setError("Viewer failed to initialize (no shared workspace).");
          return;
        }

        // Prefer Cornerstone's real per-slice IPP (keyed by the stable
        // per-list-index imageId — imageIds is built in `instances` order by
        // buildS3ImageIds) over the canonical sidecar, which usually omits
        // image_position_patient. Resolved lazily inside the geometry so the
        // streamed real IPP supersedes the synthesized eager-cache value once
        // the volume loads — see SessionGeometry / sliceWorldPositionFromMetadata
        // (PR #544). Otherwise anatomicSign is 0 → -1, backwards on
        // plane-reversed coronal/sagittal reformats.
        const resolveIpp: IppResolver = (listIndex) =>
          sliceWorldPositionFromMetadata(cornerstone.metaData, imageIds[listIndex]);
        // MPR reslice panes navigate the viewport's reoriented plane —
        // cornerstone owns the slice count (getNumberOfSlices) and index nav
        // (jumpToSlice) — so they use a viewport-backed reslice geometry rather
        // than the axial IPP list. The count is only correct after the volume
        // actor attaches, so totalSlices() is read lazily (re-read post-attach
        // below). Everything else on the mount path is identical.
        const geometry = reslicePlane
          ? createResliceGeometry(
              reslicePlane,
              () =>
                getOrCreateSharedRenderingEngine().getViewport(viewportId) as unknown as {
                  getNumberOfSlices?: () => number;
                },
            )
          : createStudyMetadataGeometry(instances, plane, resolveIpp);
        totalSlicesRef.current = geometry.totalSlices();

        const session = new ViewportSession({
          slotId: viewportId,
          seriesId,
          volumeId,
          geometry,
          onWarn: (code, detail) => {
            logger.warn("[SessionVolumeMount] %s %o", code, detail);
          },
        });
        sessionRef.current = session;

        // ViewportWorkspace.addSession expects a RecoverableSession (id /
        // recover / downgrade) so the session participates in context-loss
        // recovery. ViewportSession doesn't implement that surface yet, so wrap
        // it in a thin adapter: recover() re-attaches the strategy onto the
        // rebuilt engine; downgrade() is a no-op for now (Cpu2d swap is a later
        // increment — see TODO(A4-next)).
        const recoverable: RecoverableSession = {
          id: viewportId,
          recover: async () => {
            const el = elementRef.current;
            const strat = strategyRef.current;
            if (!el || !strat) return;
            await session.attachStrategy(strat, el);
          },
          downgrade: async () => {
            // TODO(A4-next): swap to Cpu2dStrategy on persistent GPU loss.
            logger.warn(
              "[SessionVolumeMount] downgrade requested but Cpu2d swap is not implemented yet (A4 render+scroll only)",
            );
          },
        };
        workspace.addSession(recoverable);

        // Select the render strategy. "volume" preferred (GPU volume actor),
        // "stack" fallback (StackViewport over imageIds). Both honor the same
        // RenderStrategy contract, so the session/navigation lifecycle below is
        // identical — that's the structural fix: scroll/sign can't diverge by
        // render kind (#570/#575).
        const strategy: RenderStrategy =
          viewportKind === "stack"
            ? new Cpu2dStrategy({
                viewportId,
                getEngine: () => getOrCreateSharedRenderingEngine() as unknown as RenderingEngine,
                imageIds,
                shouldFlipHorizontal: () => flipRadiologicalLR,
              })
            : new GpuVolumeStrategy({
                viewportId,
                getEngine: () => getOrCreateSharedRenderingEngine() as unknown as RenderingEngine,
                // Keep the camera on the series' true orientation on the
                // segmentation surface (skip cardinal snap) so IPM-placed
                // contours on oblique companions don't render across slices.
                shouldSnapCamera: () => shouldCardinalSnapForSurfaces(viewerSurfacesRef.current),
                shouldFlipHorizontal: () => flipRadiologicalLR,
                orientation: reslicePlane,
              });
        strategyRef.current = strategy;

        // Progressive low-res-first: attach a 128²-geometry low-res companion
        // volume FIRST (built from ranged ÷4 previews — first paint in ~1s),
        // then fall through to the full attach below, which swaps the viewport
        // to the full volume. Keep the low-res visible until the full volume has
        // its first decoded frame (bounded) so the swap shows content, not black.
        // Flag-gated + best-effort: any failure just proceeds to the full attach.
        if (progressiveVolume) {
          try {
            const lrv = await import("@/utils/lowResVolume");
            const lowResIds = lrv.registerLowResSeriesMetadata(cornerstone, imageIds);
            if (lowResIds.length && (strategy as GpuVolumeStrategy).attach) {
              const lowVolId = lrv.toLowResVolumeId(volumeId);
              const lowVol = await cornerstone.volumeLoader.createAndCacheVolume(lowVolId, {
                imageIds: lowResIds,
              });
              lowVol.load();
              await (strategy as GpuVolumeStrategy).attach(element, lowVolId);
              if (aborted) return;
              logger.info("[progressive] low-res preview shown", {
                seriesId,
                lowResFrames: lowResIds.length,
                lowVolId,
              });
              await Promise.race([
                fullFirstFrame ?? Promise.resolve(),
                new Promise<void>((r) => setTimeout(r, 4000)),
              ]);
              if (aborted) return;
            }
          } catch (lrErr) {
            logger.warn("[SessionVolumeMount] low-res preview failed; using full only", {
              seriesId,
              err: String(lrErr),
            });
          }
        }

        // Volume path: the volume is cached (above), so the strategy's
        // setVolumesForViewports inside attach is safe. Stack path: attach is
        // setStack over imageIds, no volume needed.
        await session.attachStrategy(strategy, element);
        if (aborted) return;

        // Land on the requested initial slice (clamped) and bridge it out so
        // the slice-readout overlay starts in sync. The start differs by
        // strategy:
        //   - Stack: resolveStackStartIndex — middle default, pendingJump
        //     override. The slot sliceIndex is 0 on a fresh axial assignment, so
        //     keying off it would start the weak-GPU axial fallback at the black
        //     edge slice; legacy StackViewport started mid-stack (and a
        //     finding-jump to a not-yet-displayed series lands on its slice). The
        //     onSliceChange bridge below corrects the slot sliceIndex to this
        //     start, exactly like legacy.
        //   - Volume: honor externalSliceIndex (the slot SSoT — already seeded to
        //     middle for cor/sag by the grid), else startAtMiddleSlice. A
        //     cross-series jump arrives via the volume-ready gate
        //     (promotePendingJump → externalSliceIndex), read through its ref
        //     because the promotion can land before attach resolves.
        // Reslice panes: the reslice slice count is only known once the volume
        // actor attached (getNumberOfSlices), so re-read it now, and ALWAYS open
        // mid-volume — the slot's externalSliceIndex (0 on an auto-filled cor/sag
        // slot) would otherwise land on the black edge slice (I1).
        if (reslicePlane) {
          totalSlicesRef.current = geometry.totalSlices();
          if (totalSlicesRef.current <= 1) {
            // getNumberOfSlices() wasn't ready at re-read (viewport actor race).
            // The pane would latch at the black edge slice (0) — the very bug
            // this fixes — so surface it rather than fail silently. Expected
            // never to fire: the count derives from volume dimensions set
            // synchronously at createAndCacheVolume, not from streamed pixels.
            logger.warn(
              "[SessionVolumeMount] reslice slice count unavailable post-attach (getNumberOfSlices→%d) — pane may open on the edge slice. seriesId=%s plane=%s",
              totalSlicesRef.current,
              seriesId,
              reslicePlane,
            );
          }
        }
        const total = totalSlicesRef.current;
        const initial =
          viewportKind === "stack"
            ? resolveStackStartIndex(pendingJumpRef.current, total)
            : reslicePlane
              ? Math.floor(total / 2)
              : clampIndex(
                  externalSliceIndexRef.current ?? (startAtMiddleSlice ? Math.floor(total / 2) : 0),
                  total,
                );
        currentSliceRef.current = initial;
        session.setSlice(initial);
        onSliceChangeRef.current?.(initial, totalSlicesRef.current);
        // The landed-index listener can miss this first slice while the volume
        // bootstraps (currentImageId null), so request its frame here too.
        if (viewportKind === "volume" && !reslicePlane) {
          prioritizeVolumeFrame(volumeId, imageIds[initial]);
        }

        // ── Anatomic-up sign — SINGLE source for the unified path (#570 fix) ──
        // Bridge the geometry's anatomicSign() out NOW (best-available value:
        // sidecar/synthesized IPP before the real per-slice IPP streams) so the
        // wheel/scrollbar/↑↓ have a direction immediately. The post-stream flip
        // (onVolumeLoadingCompleted, below) re-reads + re-bridges once the real
        // IPP supersedes. Fired by the mount's OWN readiness — not volumeStatus —
        // so it lands for BOTH strategies (the legacy effect gated this on a
        // volume-only signal, so the corrected sign never reached the stack
        // path → reformat scroll inverted).
        onAnatomicSignChangeRef.current?.(geometry.anatomicSign());

        // Coarse displayable signal: the strategy has attached and the first
        // frame is showable, so flip the parent's `isViewportDisplayable` gate
        // on (renders ViewportOverlay / OrientationMarkers / slice-readout).
        // Report loaded === total so the parent doesn't show a stuck progress
        // bar — per-frame streaming progress is TODO(A4-next).
        onLoadingStateChangeRef.current?.(false, total, total, true);

        // ── Initial VOI + invert from props (relocated from VolumeViewport
        //    942-957 / 2019-2034) ─────────────────────────────────────────────
        // Applied now that the strategy attached a volume actor — setProperties
        // requires the viewport to own a volume. Without this the CT renders at
        // Cornerstone's auto min/max window instead of the slot windowing.
        {
          const vp = getOrCreateSharedRenderingEngine().getViewport(viewportId) as unknown as {
            setProperties: (
              p: { voiRange?: { lower: number; upper: number }; invert?: boolean },
              volumeId?: string,
            ) => void;
          } | null;
          if (vp) {
            // setProperties' 2nd arg is the target volumeId on a VOLUME
            // viewport (apply to that actor); on a STACK viewport it is
            // `suppressEvents` (boolean) — passing the volumeId string there is
            // wrong. Pass volumeId only on the volume path; undefined on stack
            // (falsy → suppressEvents off, applies to the stack's single image).
            const volumeIdArg = viewportKind === "volume" ? volumeId : undefined;
            const wc = windowCenterRef.current;
            const ww = windowWidthRef.current;
            if (wc !== undefined && ww !== undefined) {
              try {
                vp.setProperties(
                  { voiRange: { lower: wc - ww / 2, upper: wc + ww / 2 } },
                  volumeIdArg,
                );
                lastAppliedWindowRef.current = { center: wc, width: ww };
              } catch (e) {
                logger.debug("[SessionVolumeMount] initial VOI apply skipped:", e);
              }
            }
            if (isInvertedRef.current !== undefined) {
              try {
                vp.setProperties({ invert: isInvertedRef.current }, volumeIdArg);
              } catch (e) {
                logger.debug("[SessionVolumeMount] initial invert apply skipped:", e);
              }
            }
            getOrCreateSharedRenderingEngine().renderViewports([viewportId]);
          }
        }

        // ── Bind-site sync of any outstanding AI jump (first-frame gate) ─────
        // Relocated from VolumeViewport.tsx:1096-1139. The aiJumpTargetAtom
        // effect (below) may have fired while the strategy was still attaching →
        // it bailed WITHOUT setting the cursor; catch the unfired target now
        // that attachStrategy() resolved + the viewport owns a volume actor.
        // Shares the FoR gate + warn ids with the effect.
        {
          const ai = getDefaultStore().get(aiJumpTargetAtom);
          if (ai && lastFiredAiJumpTriggerRef.current !== ai.trigger) {
            const ownFor =
              studyMetadata?.series_metadata?.[seriesId]?.frame_of_reference_uid ?? null;
            const gate = checkAiJumpForGate(ai, seriesId, ownFor);
            if (!gate.ok) {
              logAiJumpBail(gate.reason, "bind", ai, seriesId, ownFor);
              lastFiredAiJumpTriggerRef.current = ai.trigger;
              // Same real-geometry preference as the effect site (#921).
            } else if (
              session.showWorldNow(
                resolveAiJumpWorld(ai, cornerstone.metaData, aiJumpImageIds(ai)).world,
              )
            ) {
              // Do NOT bridge ai.sliceIndex — that's the TARGET series' index,
              // not this viewport's landed index. The VOLUME_NEW_IMAGE listener
              // owns the readout: jumpToWorld moves the camera → that listener
              // resolves + writes the correct landed list-index (#468 authority).
              lastFiredAiJumpTriggerRef.current = ai.trigger;
              logger.info(
                "ai_slice_jump_bind_sync seriesId=%s targetSeriesId=%s sliceIndex=%d trigger=%d",
                seriesId,
                ai.seriesId,
                ai.sliceIndex,
                ai.trigger,
              );
            }
          }
        }

        // ── Tool-group membership (A4-seg-2) ────────────────────────────────
        // Relocated from VolumeViewport.tsx:476-499. Join the SHARED
        // (annotating) tool group BEFORE seg setup so the contour-segmentation
        // RENDER path can resolve this viewport's tool group — without it,
        // SegmentationRenderingEngine._addPlanarFreeHandToolIfAbsent reads
        // `getToolGroupForViewport(viewportId).hasTool(...)` on undefined and
        // throws, and the saved mask fails to render. Joining the group also
        // enables the drawing tools (lasso/bump/region-grow).
        //
        // ASSUMPTION: the `?newViewport=1` flag site never renders a readonly
        // viewport (VolumeViewport's readonly path uses a separate
        // navigation-only tool group). So we only handle the shared annotating
        // group here; a readonly branch would be dead code on this path.
        const { ToolGroupManager } = getCornerstoneTools();
        const toolGroupId = `${SHARED_RENDERING_ENGINE_ID}_toolGroup`;
        let toolGroup = ToolGroupManager.getToolGroup(toolGroupId);
        if (!toolGroup) {
          toolGroup = ToolGroupManager.createToolGroup(toolGroupId);
          if (toolGroup) configureToolGroup(toolGroup);
        }
        if (toolGroup) {
          toolGroup.addViewport(viewportId, SHARED_RENDERING_ENGINE_ID);
          toolGroupRef.current = toolGroup;
          toolGroupAdded = true;
          setToolsReady(true);
        }

        // ── Segmentation labelmap + representations (A4-seg-1; C2) ──────────
        // Relocated from VolumeViewport.tsx:964-1048 (GPU path). Builds the
        // per-series derived labelmap volume (each series needs its own for
        // save rasterization) + registers its Labelmap rep on this viewport,
        // then PUBLISHES a readiness signal. C2: this pane no longer activates a
        // contour seg or sets the gate atom — the layout owner does both, once,
        // deterministically (see the `seriesSegReadyFamily` write below).
        const segmentationId = `${studyId}_${seriesId}_segmentation`;

        const runSegmentationSetup = async (): Promise<boolean> => {
          const srcVolume: any = cornerstone.cache.getVolume(volumeId);
          const dimsReady = Array.isArray(srcVolume?.dimensions) && srcVolume.dimensions[0] != null;
          if (!dimsReady) return false;
          const { segmentation, Enums: ToolEnums } = getCornerstoneTools();
          try {
            await segmentation.removeSegmentation(segmentationId);
          } catch (e) {
            logger.debug("[SessionVolumeMount] removeSegmentation (expected on first load):", e);
          }
          if (aborted) return true;
          // Guard against re-creating an already-cached derived volume. The
          // `removeSegmentation` above unregisters the abstract segmentation but
          // does NOT evict its labelmap volume from the cornerstone cache, so any
          // re-run of this setup — the unified path's re-attach (viewportKind/
          // enableSegmentation effect deps), a remount/HMR, or the cold-load
          // retry below — would call createAndCacheDerivedVolume on the still-
          // cached id and throw "volumeId already in cache". That throw lands
          // BEFORE addSegmentationRepresentations, so the rep fails to
          // (re)register on the viewport and the labelmap the draw tools write to
          // desyncs from what renders — the corrupted mask after editing. Reuse
          // the cached derived volume (its already-drawn voxels survive
          // removeSegmentation) and fall through to re-register the rep. Mirrors
          // the pre-unification StackViewport guard that the consolidation lost.
          if (!cornerstone.cache.getVolume(segmentationId)) {
            cornerstone.volumeLoader.createAndCacheDerivedVolume(volumeId, {
              volumeId: segmentationId,
            });
          }
          segmentation.addSegmentations([
            {
              segmentationId,
              representation: {
                type: ToolEnums.SegmentationRepresentations.Labelmap,
                data: { volumeId: segmentationId },
              },
            },
          ]);
          segmentation.addSegmentationRepresentations(viewportId, [
            {
              segmentationId,
              type: ToolEnums.SegmentationRepresentations.Labelmap,
            },
          ]);
          segmentationIdRef.current = segmentationId;

          // Re-check the unmount signal right before the React/atom writes. The
          // registration calls above are synchronous, but a cleanup (unmount /
          // series-swap) could have flipped `aborted` during the earlier
          // removeSegmentation await. Writing React state (setSegReady) / the
          // readiness signal after teardown would re-ungate the layout owner
          // against a disposed viewport. Return true so the caller treats this
          // as "handled" (no deferral/retry), not a cold-load failure.
          if (aborted) return true;

          // C2: this pane does NOT activate a contour seg or set the gate atom
          // here anymore (that was the per-pane last-writer-wins race). It only
          // PUBLISHES that its labelmap + segmentation are registered. The
          // layout-level owner (HangingProtocolGrid) reads this, picks the
          // priority series' labelmap deterministically, pre-initializes segment
          // metadata on the paired contour seg, then sets `segmentationVolumeId`
          // ONCE. This pane's contour-rep effect (below) then activates THAT
          // shared contour seg on this viewport (so contours render + draw here).
          setSeriesSegReady(segmentationId);
          setSegReady(true);
          return true;
        };

        // Seg setup is gated on `enableSegmentation`. Volume path: always on
        // (region-grow is safe on a real volume actor). Stack path: only the
        // weak-GPU fallback on a geometrically-sound series — on a blocker /
        // is_reconstructable=false series the source volume wasn't built (see
        // needsSourceVolume above) and region-grow is unsafe, so seg stays off
        // and the pane renders nav/window-level tools only (the drawing tools
        // are gated off downstream by the absent segmentationVolumeId).
        let segSetupDone = !enableSegmentation; // treat "no seg" as handled (no retry)
        if (enableSegmentation) {
          try {
            segSetupDone = await runSegmentationSetup();
          } catch (e) {
            logger.error("[SessionVolumeMount] Segmentation setup failed:", e);
          }
        }
        if (!segSetupDone) {
          // Cold-load case: source-volume dimensions not yet hydrated. Stash
          // the retry for the VOLUME_LOADING_COMPLETED listener (below) to fire.
          pendingSegSetupRef.current = async () => {
            // Skip if a prior completion event is still draining this retry —
            // prevents concurrent runSegmentationSetup (double-register +
            // orphaned controller). On failure we leave pendingSegSetupRef set
            // so a later completion event can retry.
            if (segSetupInFlightRef.current) return;
            segSetupInFlightRef.current = true;
            try {
              const ok = await runSegmentationSetup();
              if (ok) pendingSegSetupRef.current = null;
            } catch (e) {
              logger.error("[SessionVolumeMount] Deferred segmentation setup failed:", e);
            } finally {
              segSetupInFlightRef.current = false;
            }
          };
        }

        // Drain the deferred seg setup once the source volume finishes loading.
        // Mirrors VolumeViewport.tsx:519-552 (the seg-draining part only).
        // CORNERSTONE core events use the bare CORNERSTONE_* prefix (NOT the
        // CORNERSTONE_TOOLS_* tools prefix) — matched verbatim from VV.
        const onVolumeLoadingCompleted = (evt: any) => {
          if (evt?.detail?.volumeId !== volumeId) return;
          const pending = pendingSegSetupRef.current;
          if (pending) void pending();
          // Post-stream anatomic-sign flip (#570 fix), BOTH paths. Now that the
          // source volume has streamed, the real per-slice IPP for list-index 0
          // and N-1 is in Cornerstone's metadata, so geometry.anatomicSign()
          // re-reads the real geometry and supersedes the synthesized
          // eager-cache value the initial mount-ready write used. This is the
          // moment the corrected sign lands on the STACK path too (the volume is
          // built + loaded for the labelmap on the weak-GPU reconstructable
          // fallback), which the legacy volumeStatus-gated effect never reached.
          onAnatomicSignChangeRef.current?.(geometry.anatomicSign());
          // Cold-load VOI re-apply is volume-only: the cold-load voiLut-hydration
          // race is a GPU-volume-actor behavior. On the stack path the VOI is
          // applied immediately on the loaded image (no streaming actor), so skip
          // the re-apply — but the seg-drain above still runs (the labelmap source
          // volume streams on both paths when seg is enabled).
          if (viewportKindRef.current !== "volume") return;
          // Cold-load VOI re-apply (mirrors VolumeViewport 519-547): the volume's
          // voiLut can hydrate late, after which Cornerstone auto-picks min/max
          // and clobbers the window we applied on attach. Re-assert it here
          // unconditionally: lastAppliedWindowRef records what we asked for, not
          // what Cornerstone now shows, so it cannot detect the clobber.
          const wc = windowCenterRef.current;
          const ww = windowWidthRef.current;
          if (wc === undefined || ww === undefined) return;
          try {
            const vp = getOrCreateSharedRenderingEngine().getViewport(viewportId) as unknown as {
              setProperties: (
                p: { voiRange?: { lower: number; upper: number } },
                volumeId?: string,
              ) => void;
              render: () => void;
            } | null;
            if (vp) {
              vp.setProperties({ voiRange: { lower: wc - ww / 2, upper: wc + ww / 2 } }, volumeId);
              vp.render();
              lastAppliedWindowRef.current = { center: wc, width: ww };
            }
          } catch (e) {
            logger.debug("[SessionVolumeMount] cold-load VOI re-apply skipped:", e);
          }
        };
        cornerstone.eventTarget.addEventListener(
          VOLUME_LOADING_COMPLETED_EVENT as any,
          onVolumeLoadingCompleted,
        );
        volumeLoadingCompletedListenerRef.current = onVolumeLoadingCompleted;

        // ── VOI mirror-out (drag-windowing) — relocated from VolumeViewport
        //    615-626 ─────────────────────────────────────────────────────────
        // WindowLevel-tool drags fire CORNERSTONE_VOI_MODIFIED on the ELEMENT
        // (CORE event, bare prefix, bubble phase — NOT the TOOLS prefix, NOT
        // eventTarget). Mirror it to onWindowChange so the slot windowing atom +
        // the W:/L: overlay track the drag. The lastAppliedWindowRef write
        // suppresses the echo back into the prop-change apply effect.
        const onVoiModified = (e: any) => {
          const voiRange = e.detail?.range;
          if (!voiRange) return;
          const center = Math.round((voiRange.lower + voiRange.upper) / 2);
          const width = Math.round(voiRange.upper - voiRange.lower);
          lastAppliedWindowRef.current = { center, width };
          onWindowChangeRef.current?.(center, width);
        };
        element.addEventListener("CORNERSTONE_VOI_MODIFIED" as any, onVoiModified);
        voiModifiedListenerRef.current = onVoiModified;

        // ── Camera mirror-out (zoom/pan) — relocated from VolumeViewport
        //    628-641 ─────────────────────────────────────────────────────────
        // CORNERSTONE_CAMERA_MODIFIED (CORE event, element target). Read the
        // live camera via the strategy (inherits its getPan undefined-guard),
        // push it into the session snapshot so context-loss recover()/re-attach
        // restores the user's zoom/pan (ViewportSession replays this.camera),
        // and mirror out via onZoomChange + onCameraChange.
        const onCameraModified = () => {
          const strat = strategyRef.current;
          if (!strat) return;
          const cam = strat.getCamera();
          // recordCamera (snapshot-only) — NOT setCamera, which would re-apply
          // to the strategy and risk a CAMERA_MODIFIED feedback loop.
          sessionRef.current?.recordCamera(cam);
          onZoomChangeRef.current?.(cam.zoom);
          // Mutable copy — CameraState.pan is readonly; onCameraChange (mirroring
          // VolumeViewport) takes a mutable [number, number].
          onCameraChangeRef.current?.(cam.zoom, [cam.pan[0], cam.pan[1]]);
        };
        element.addEventListener("CORNERSTONE_CAMERA_MODIFIED" as any, onCameraModified);
        cameraModifiedListenerRef.current = onCameraModified;

        // ── Landed-slice listener — relocated from VolumeViewport 554-612 ────
        // Fires whenever the viewport's slice changes — including external
        // camera moves (handleSelect's direct jumpToWorld, cross-plane jumps,
        // reference-line drags) that bypass session.setSlice. It is the
        // AUTHORITY on the landed list-index, bridging it to currentSliceRef +
        // onSliceChange so the slot readout tracks the camera. CORE event,
        // CAPTURE phase (dispatched on a child .viewport-element div —
        // bubble-phase on the outer element misses it).
        //
        // The event + index semantics differ by strategy:
        //  - Volume: CORNERSTONE_VOLUME_NEW_IMAGE carries a K-step `imageIndex`;
        //    translate K-step → list-index via the viewport's current imageId
        //    (#401 reversed-HTJ2K), skipping the unresolved bootstrap case
        //    (#468 — don't write a wrong value; the follow-up event resolves).
        //  - Stack: CORNERSTONE_STACK_NEW_IMAGE carries `imageIdIndex`, already
        //    a list-index (setStack uses the imageIds list order verbatim — no
        //    K-step reorder), so it bridges directly.
        const onVolumeNewImage = (e: any) => {
          const kStepIndex = e.detail?.imageIndex;
          if (e.detail?.viewportId !== viewportId) return;
          if (kStepIndex === undefined) return;
          // Reslice (MPR) pane: cornerstone's `imageIndex` IS the reoriented
          // plane's slice index — use it directly. kStepToListIndex maps into
          // the AXIAL imageIds list (meaningless here), and `ids.length` is the
          // axial count; report the live reslice slice count instead so the
          // readout denominator is the coronal/sagittal total, not /axial.
          if (reslicePlane) {
            currentSliceRef.current = kStepIndex;
            onSliceChangeRef.current?.(kStepIndex, geometry.totalSlices());
            return;
          }
          const ids = imageIdsRef.current ?? [];
          let currentImageId: string | null = null;
          try {
            const vp = getOrCreateSharedRenderingEngine().getViewport(viewportId) as unknown as {
              getCurrentImageId?: () => string | null;
            } | null;
            currentImageId = vp?.getCurrentImageId?.() ?? null;
          } catch (e2) {
            logger.debug("[SessionVolumeMount] getCurrentImageId (volume new image):", e2);
          }
          const { listIndex, resolved } = kStepToListIndex({
            kStepIndex,
            imageIds: ids,
            currentImageId,
          });
          if (!resolved) {
            // currentImageId null (volume bootstrapping right after jumpToWorld)
            // or not in the list — acting on the kStep fallback would snap the
            // readout to the wrong index (#401). Skip + warn; the follow-up
            // VOLUME_NEW_IMAGE post-jump resolves cleanly.
            logger.warn(
              "viewer_kstep_fallback_skipped seriesId=%s reason=%s currentImageId=%s kStepIndex=%d listLen=%d",
              seriesId,
              currentImageId ? "imageid_not_in_list" : "currentimageid_null",
              currentImageId ?? "<null>",
              kStepIndex,
              ids.length,
            );
            return;
          }
          currentSliceRef.current = listIndex;
          onSliceChangeRef.current?.(listIndex, ids.length);
          prioritizeVolumeFrame(volumeId, ids[listIndex]);
        };

        const onStackNewImage = (e: any) => {
          if (e.detail?.viewportId !== viewportId) return;
          const imageIdIndex = e.detail?.imageIdIndex;
          if (typeof imageIdIndex !== "number") return;
          const ids = imageIdsRef.current ?? [];
          currentSliceRef.current = imageIdIndex;
          onSliceChangeRef.current?.(imageIdIndex, ids.length);
        };

        const landedEvent =
          viewportKind === "stack" ? STACK_NEW_IMAGE_EVENT : "CORNERSTONE_VOLUME_NEW_IMAGE";
        const landedHandler = viewportKind === "stack" ? onStackNewImage : onVolumeNewImage;
        element.addEventListener(landedEvent as any, landedHandler, true);
        landedImageListenerRef.current = landedHandler;
        landedImageEventRef.current = landedEvent;
      } catch (err) {
        if (aborted) return;
        logger.error(
          "[SessionVolumeMount] viewer_session_volume_mount_failed seriesId=%s err=%o",
          seriesId,
          err,
        );
        setError(
          err instanceof Error ? err.message : "Failed to attach volume (new viewport path)",
        );
      }
    })().catch((err) => {
      if (aborted) return;
      logger.error("[SessionVolumeMount] mount IIFE rejected seriesId=%s err=%o", seriesId, err);
      setError("Viewer failed to initialize (new viewport path). Reload the page to retry.");
    });

    return () => {
      aborted = true;

      // ── Segmentation teardown (A4-seg-1) ────────────────────────────────
      // Remove the volume-loading-completed listener so a deferred retry can't
      // fire against a disposed viewport.
      if (volumeLoadingCompletedListenerRef.current) {
        try {
          getCornerstone().eventTarget.removeEventListener(
            VOLUME_LOADING_COMPLETED_EVENT as any,
            volumeLoadingCompletedListenerRef.current,
          );
        } catch (e) {
          logger.debug("[SessionVolumeMount] removeEventListener (volume loading completed):", e);
        }
        volumeLoadingCompletedListenerRef.current = null;
      }
      // Remove the element-level VOI + camera mirror listeners (relocated from
      // VolumeViewport 687-698). `element` is captured at IIFE start; fall back
      // to elementRef for robustness.
      {
        const el = elementRef.current;
        if (el && voiModifiedListenerRef.current) {
          el.removeEventListener("CORNERSTONE_VOI_MODIFIED" as any, voiModifiedListenerRef.current);
        }
        if (el && cameraModifiedListenerRef.current) {
          el.removeEventListener(
            "CORNERSTONE_CAMERA_MODIFIED" as any,
            cameraModifiedListenerRef.current,
          );
        }
        // The landed-image listener was registered with capture=true → must
        // remove with the same capture flag AND the same event name the mount
        // chose by strategy (VOLUME_NEW_IMAGE vs STACK_NEW_IMAGE).
        if (el && landedImageListenerRef.current && landedImageEventRef.current) {
          el.removeEventListener(
            landedImageEventRef.current as any,
            landedImageListenerRef.current,
            true,
          );
        }
        voiModifiedListenerRef.current = null;
        cameraModifiedListenerRef.current = null;
        landedImageListenerRef.current = null;
        landedImageEventRef.current = null;
        lastAppliedWindowRef.current = null;
      }
      imageIdsRef.current = null;
      // Drop the seg representations on this viewport so we don't leak refs
      // against a viewportId that's about to be disabled (mirrors VV:723-729).
      try {
        const { segmentation } = getCornerstoneTools();
        segmentation.removeSegmentationRepresentations(viewportId, {});
      } catch (e) {
        logger.debug("[SessionVolumeMount] removeSegmentationRepresentations:", e);
      }
      // Detach from the shared tool group so subsequent mounts don't
      // accumulate stale viewport ids (mirrors VV:732-738). Capture the
      // tool group into a local once before nulling the ref (mirrors VV:666),
      // and only remove a viewport this mount actually added.
      const toolGroup = toolGroupRef.current as any;
      if (toolGroupAdded && toolGroup) {
        try {
          toolGroup.removeViewport(viewportId, SHARED_RENDERING_ENGINE_ID);
        } catch (e) {
          logger.debug("[SessionVolumeMount] toolGroup.removeViewport:", e);
        }
      }
      toolGroupRef.current = null;
      setToolsReady(false);

      segmentationIdRef.current = null;
      volumeIdRef.current = null;
      pendingSegSetupRef.current = null;
      setSegReady(false);
      // Retract this pane's readiness signal so the layout owner won't pick a
      // torn-down series as the gate on the next ready-set.
      setSeriesSegReady(null);
      // NOTE: deliberately do NOT null segmentationVolumeIdAtom here. The OLD
      // VolumeViewport never sets it back to null on unmount (verified: the
      // only setSegmentationVolumeId(...) call sets the id, never null), so
      // matching that avoids behavior divergence between the two paths. The
      // layout owner is now the sole writer of the gate on the new path.

      // Dispose the strategy/session explicitly: ViewportWorkspace.dispose()
      // only destroys the engine + clears the session registry — it does NOT
      // call back into per-session strategy.dispose(). Disabling this
      // viewport's element first keeps sibling panes alive (strategy.dispose
      // calls engine.disableElement, NOT engine.destroy). This runs in BOTH
      // the shared and per-pane cases — it only tears down THIS pane's
      // viewport, never the engine.
      try {
        strategyRef.current?.dispose();
      } catch (err) {
        logger.warn("[SessionVolumeMount] strategy dispose failed:", err);
      }
      // A4 consolidation C1: only DEREGISTER this pane's session from the layout
      // workspace — the engine + context-loss listeners stay alive for the other
      // panes and are destroyed exactly once when the grid (workspace owner)
      // unmounts. This pane never owns/disposes a workspace.
      try {
        sharedWorkspaceRef.current?.removeSession(viewportId);
      } catch (err) {
        logger.warn("[SessionVolumeMount] shared workspace removeSession failed:", err);
      }
      strategyRef.current = null;
      sessionRef.current = null;
      viewportIdRef.current = null;
    };
    // viewportIdOverride is stable per parent instance; treated as mount-time.
    // viewportKind/enableSegmentation are included so a strategy flip (volume↔
    // stack) re-attaches the correct RenderStrategy even when seriesId/studyId/
    // studyMetadata are unchanged — on the legacy path a kind flip remounts the
    // component, so this keeps unified-path behavior equivalent and avoids a
    // stuck error pane if a series' reconstructability resolves after its id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesId, studyId, studyMetadata, viewportKind, enableSegmentation, reslicePlane]);

  // ─── Contour-rep active on the SHARED (gate) contour seg (A4-seg-1; C2) ─────
  // Mirrors VolumeViewport.tsx:1285-1287 EXACTLY: keep the Contour-typed rep for
  // the GLOBALLY-GATED segmentation registered + active on THIS viewport and
  // re-apply the active-segment color whenever the active segment index changes.
  //
  // C2: the key reads `segmentationVolumeId` (the gate the layout owner set),
  // NOT this pane's own labelmap. So every pane converges on the SAME contour
  // seg — there is no per-pane last-writer-wins race on the globally-active
  // contour segmentation (the root cause of the saved-mask 'locked' crash and
  // the draw segmentIndex desync). `ensureContourSegmentationActive` is
  // idempotent + loops every enabled viewport, so a late-mounting pane self-
  // registers the shared rep here once the gate is set. The owner already
  // pre-initialized segments[1..maxIdx] on this contour seg before flipping the
  // gate, so the saved-mask restore finds initialized segment metadata.
  useEffect(() => {
    if (!segmentationVolumeId || !segReady) return;
    ensureContourSegmentationActive(segmentationVolumeId, activeSegmentIndex);
  }, [segmentationVolumeId, activeSegmentIndex, segReady]);

  // ─── Active-tool switch (A4-seg-2) ─────────────────────────────────────────
  // Relocated from VolumeViewport.tsx:1419-1424. Set the active mouse-bound
  // tool on the shared tool group whenever the effective tool (or measure
  // refinement) changes. Gated on toolGroupRef + toolsReady so it only runs
  // after the mount effect joined the group (mirrors VV's lifecycleReady gate).
  useEffect(() => {
    if (!toolGroupRef.current || !toolsReady) return;
    switchActiveTool(toolGroupRef.current, effectiveTool, {
      measureRefinement: measureRefining,
    });
  }, [effectiveTool, measureRefining, toolsReady]);

  // ─── Bump-radius sync (A4-seg-2) ───────────────────────────────────────────
  // Relocated FAITHFULLY from VolumeViewport.tsx:1267-1278. The radius is
  // consumed both via `toolGroup.setToolConfiguration("BumpContourTool",
  // { radius })` (primary path the tool reads on mouse-down) and a module-level
  // fallback in BumpContourTool.ts. Without this wire-up the UI buttons update
  // the atom but the brush size never changes on the `?newViewport=1` path.
  // VV calls the module setter UNCONDITIONALLY before the toolGroup guard —
  // ordering replicated here. Gated on the component's `toolsReady` (NOT VV's
  // `lifecycleReady`) since that's this mount's tool-group-joined signal.
  const bumpRadius = useAtomValue(bumpRadiusAtom);

  // ─── Segmentation tool cursor (relocated from VolumeViewport:2053) ──────────
  // Sets the custom cursor (region-grow crosshair, bump brush circle, nn
  // refine cross) on the viewport element + canvas based on the active tool.
  // Cornerstone overrides CSS cursor, so the hook sets it imperatively. Without
  // this the new path shows the default cursor with no tool affordance.
  useSegmentationCursor(elementRef, activeMeasurementTool, bumpRadius);

  useEffect(() => {
    setBumpRadiusModule(bumpRadius);
    const tg = toolGroupRef.current as any;
    if (!tg || !toolsReady) return;
    try {
      tg.setToolConfiguration?.("BumpContourTool", { radius: bumpRadius });
    } catch (e) {
      logger.debug("[SessionVolumeMount] bump radius setToolConfiguration skipped:", e);
    }
  }, [bumpRadius, toolsReady]);

  // ─── Region-grow commit + auto-switch callbacks (A4-seg-2) ─────────────────
  // Relocated FAITHFULLY from VolumeViewport.tsx:1301-1417 (including the
  // cross-plane centroid jump — NOT altered here; that's a separate task,
  // A4-note-3). Flip drawing-state gating when a seed click lands (Save button
  // enables, series locks, HU overlay populates) and auto-switch to the bump
  // tool afterwards.
  //
  // Keyed by `viewportId` so a multi-plane hanging protocol dispatches to
  // whichever viewport the click landed on; the tool resolves the right entry
  // from `ctx.viewport.id` at fire time.
  //
  // Gated on `segmentationVolumeId` (the seg rep + viewportId both need to be
  // in place before wiring callbacks) and the captured viewportId.
  useEffect(() => {
    const vpId = viewportIdRef.current;
    if (!segmentationVolumeId || !vpId) return;
    setRegionGrowCommitCallback(vpId, (currentImageId: string | null = null) => {
      if (!isSegmentationActiveRef.current) return;
      setIsDrawing(true);
      setHasFirstStroke(true);
      setLockedSeriesId(seriesId);
      setAnnotationStartTs((prev: string | null) => prev ?? new Date().toISOString());

      // Cross-plane companion-viewport sync: jump every viewport to the
      // slice that contains the seed contour's world centroid. Fires once
      // per region-grow click (not per added annotation), so multi-viewport
      // seed propagation doesn't trigger N camera jumps.
      try {
        const cs = getCornerstone();
        const ct = getCornerstoneTools();
        const allAnns: any[] = ct.annotation.state.getAllAnnotations() ?? [];
        const seed = [...allAnns]
          .reverse()
          .find(
            (a: any) =>
              a?.metadata?.toolName === "PlanarFreehandContourSegmentationTool" &&
              !a?.autoGenerated &&
              !isLoadedFromMask(a) &&
              (currentImageId ? a?.metadata?.referencedImageId === currentImageId : true) &&
              (a?.data?.contour?.polyline?.length ?? 0) > 0,
          );
        const polyline: number[][] = seed?.data?.contour?.polyline ?? [];
        if (polyline.length === 0) return;
        let cx = 0,
          cy = 0,
          cz = 0;
        for (const p of polyline) {
          cx += p[0];
          cy += p[1];
          cz += p[2];
        }
        const centroid: [number, number, number] = [
          cx / polyline.length,
          cy / polyline.length,
          cz / polyline.length,
        ];
        // Find the primary viewport (one whose imageIds include the seed's
        // referencedImageId) so we can exempt it from the cross-plane jump
        // below — the user just clicked there, so re-jumping would yank the
        // camera off the slice they're looking at.
        const seedRefImageId = seed?.metadata?.referencedImageId;
        const engines = cs.getRenderingEngines?.() ?? [];
        let primaryVp: any = null;
        if (seedRefImageId) {
          for (const engine of engines) {
            for (const vp of engine.getViewports?.() ?? []) {
              const ids: string[] = (vp as any).getImageIds?.() ?? [];
              if (ids.includes(seedRefImageId)) {
                primaryVp = vp;
                break;
              }
            }
            if (primaryVp) break;
          }
        }
        // Per item 7 of the workflow spec: each viewport should land on an
        // annotated slice. Companion viewports that already have committed
        // contours for the current segment are intentionally LEFT in place.
        for (const engine of engines) {
          for (const vp of engine.getViewports?.() ?? []) {
            if (typeof (vp as any).jumpToWorld !== "function") continue;

            // Never re-jump the primary (clicked) viewport. The user just
            // clicked it, so it's already at the right slice. Under
            // progressive HTJ2K the camera focal Z can drift from the
            // displayed slice index, and jumpToWorld(centroid) would snap
            // the camera to that drift — moving the axial view "to the
            // wrong slice" the moment a seed is committed.
            if (vp === primaryVp) continue;

            const vpImageIds: string[] = (vp as any).getImageIds?.() ?? [];
            const vpImageIdSet = new Set(vpImageIds);
            const hasOtherContour = allAnns.some(
              (a: any) =>
                a?.metadata?.toolName === "PlanarFreehandContourSegmentationTool" &&
                a?.data?.segmentation?.segmentIndex === activeSegmentIndex &&
                a?.metadata?.referencedImageId &&
                vpImageIdSet.has(a.metadata.referencedImageId) &&
                a?.annotationUID !== seed?.annotationUID,
            );
            if (hasOtherContour) continue;

            try {
              (vp as any).jumpToWorld(centroid);
            } catch (err) {
              logger.debug("[SessionVolumeMount] commit jumpToWorld skipped:", err);
            }
          }
        }
      } catch (err) {
        logger.debug("[SessionVolumeMount] commit cross-plane nav failed:", err);
      }
    });
    setRegionGrowAutoSwitchCallback(vpId, () => {
      if (!isSegmentationActiveRef.current) return;
      setActiveMeasurementTool("bump");
    });
    return () => {
      setRegionGrowCommitCallback(vpId, null);
      setRegionGrowAutoSwitchCallback(vpId, null);
    };
  }, [
    segmentationVolumeId,
    seriesId,
    setIsDrawing,
    setHasFirstStroke,
    setLockedSeriesId,
    setAnnotationStartTs,
    setActiveMeasurementTool,
  ]);

  // ─── Seg-lifecycle CORNERSTONE_TOOLS_* listeners (A4-seg-3) ────────────────
  // Relocated FAITHFULLY from VolumeViewport.tsx:1426-1787. These read state via
  // refs (isSegmentationActiveRef, segmentationIdRef, volumeIdRef) so the
  // long-lived closures don't go stale, and write the Jotai atoms that the
  // (unchanged) SegmentationAnnotationPanel reads to enable Save.
  //
  //   - ANNOTATION_ADDED            → THE Save-enable path: flips isDrawing /
  //                                   hasFirstStroke + records annotationStartTs
  //                                   on the first user contour stroke.
  //   - SEGMENTATION_DATA_MODIFIED  → debounced voxel iteration → HU/volume
  //                                   overlay (setDerivedMeasurements).
  //   - ANNOTATION_COMPLETED        → bump contourVersion (3D refresh) +
  //                                   cross-plane centroid jump (UNCHANGED —
  //                                   A4-note-3 owns the jump fix).
  //   - ANNOTATION_SELECTION_CHANGE → cross-viewport sync on contour click.
  //
  // Gated on `segReady` so it only registers once the labelmap + Contour rep
  // (which populate segmentationIdRef) exist — registering earlier would mark
  // drawing state against a segmentationIdRef that's still null. activeSegment-
  // Index stays in the deps (mirrors VV:1782) so the measurement/jump closures
  // read the current segment without a ref.
  useEffect(() => {
    if (!segReady) return;

    // Guard: Cornerstone may not be initialized yet if this effect runs before
    // the mount IIFE completes. Matches VolumeViewport.tsx:1430-1435 — bail and
    // re-run when activeSegmentIndex/segReady change after initialization.
    let eventTarget: ReturnType<typeof getCornerstone>["eventTarget"];
    try {
      ({ eventTarget } = getCornerstone());
    } catch (e) {
      logger.debug(
        "[SessionVolumeMount] seg-lifecycle listeners deferred (cornerstone not ready):",
        e,
      );
      return;
    }

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    // #725: separate timer for the contour-edit → 3D-refresh debounce (below).
    let contourEditDebounce: ReturnType<typeof setTimeout> | null = null;

    const onSegmentationModified = (evt: any) => {
      const segId = evt.detail?.segmentationId;
      const labelmapId = segmentationIdRef.current;
      if (!segId || !labelmapId) return;
      // Accept events from either the paired labelmap seg or its contour seg
      // (see getContourSegmentationId). Both are written during a single
      // user stroke and either is sufficient to mark drawing state.
      const contourId = getContourSegmentationId(labelmapId);
      if (segId !== labelmapId && segId !== contourId) return;

      // Drawing-state gate: only flip isDrawing when the user has an
      // annotation form open. Background voxel writes (niftiMaskLoader mask
      // restore on case open) shouldn't disable the Add button.
      //
      // Asymmetry note: when inactive AND the event is for the labelmap
      // (mask restore writes voxels directly, no contour event), we still
      // fall through to the voxel-iteration block below so derived
      // measurements (volume/HU stats) update from the restored mask.
      // Inactive + contour-only events have no measurement payload, so
      // those bail early. Active in either case → flip drawing state.
      if (!isSegmentationActiveRef.current) {
        if (segId !== labelmapId) return;
      } else {
        setIsDrawing(true);
        setAnnotationStartTs((prev: string | null) => prev ?? new Date().toISOString());
      }

      // The voxel-iteration measurement work below only applies to the
      // labelmap volume — skip it for contour-only events.
      if (segId !== labelmapId) return;

      // Debounce the expensive voxel iteration — on large volumes (500+ slices) iterating
      // the entire scalar array on every brush stroke would freeze the UI thread.
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        // Derive measurements from the labelmap
        try {
          const cornerstone = getCornerstone();
          const volume = cornerstone.cache.getVolume(segId) as any;
          if (!volume) return;

          const { dimensions, spacing } = volume;
          const scalarData: Uint8Array =
            typeof volume.getScalarData === "function" ? volume.getScalarData() : volume.scalarData;
          if (!scalarData) return;

          const [dx, dy, dz] = spacing as [number, number, number];
          const [cols, rows, slices] = dimensions as [number, number, number];
          const voxelVolume = dx * dy * dz;

          let count = 0;
          let huMin = Infinity,
            huMax = -Infinity,
            huSum = 0;
          const sliceSet = new Set<number>();
          let minR = rows,
            maxR = 0,
            minC = cols,
            maxC = 0,
            minS = slices,
            maxS = 0;

          // CT volume for HU values
          const ctVolume = cornerstone.cache.getVolume(volumeIdRef.current ?? "") as any;
          const ctScalar = ctVolume?.getScalarData
            ? ctVolume.getScalarData()
            : ctVolume?.scalarData;
          const sliceSize = cols * rows;

          // Single pass: collect voxel count, HU stats, slice set, and bounding box
          for (let i = 0; i < scalarData.length; i++) {
            if (scalarData[i] === activeSegmentIndex) {
              count++;
              const s = Math.floor(i / sliceSize);
              const rem = i % sliceSize;
              const r = Math.floor(rem / cols);
              const c = rem % cols;
              sliceSet.add(s);
              if (r < minR) minR = r;
              if (r > maxR) maxR = r;
              if (c < minC) minC = c;
              if (c > maxC) maxC = c;
              if (s < minS) minS = s;
              if (s > maxS) maxS = s;
              if (ctScalar) {
                const hu = ctScalar[i] as number;
                if (hu < huMin) huMin = hu;
                if (hu > huMax) huMax = hu;
                huSum += hu;
              }
            }
          }

          if (count === 0) return;

          // Estimate long/short axis from bounding box in mm
          // (full eigenvector decomposition deferred to backend)
          const extX = (maxC - minC) * dx;
          const extY = (maxR - minR) * dy;
          const extZ = (maxS - minS) * dz;
          const extents = [extX, extY, extZ].sort((a, b) => b - a);

          setDerivedMeasurements({
            volume_mm3: count * voxelVolume,
            long_axis_mm: extents[0],
            short_axis_mm: extents[1],
            slice_count: sliceSet.size,
            hu_min: ctScalar ? huMin : 0,
            hu_max: ctScalar ? huMax : 0,
            hu_avg: ctScalar && count > 0 ? huSum / count : 0,
          });
        } catch (e) {
          // Measurement derivation is best-effort; backend recomputes on save
          logger.debug("[SessionVolumeMount] derived measurement pass skipped:", e);
        }
      }, 150);
    };

    eventTarget.addEventListener(
      "CORNERSTONE_TOOLS_SEGMENTATION_DATA_MODIFIED" as any,
      onSegmentationModified,
    );

    // SEGMENTATION_DATA_MODIFIED only fires when labelmap voxels change.
    // Pure-contour strokes (lasso, region-grow) don't touch the labelmap
    // without polySeg, so the badge would never appear. Listen for
    // ANNOTATION_ADDED on PlanarFreehandContourSegmentationTool annotations
    // tagged with one of our segmentationIds to flip drawing state on the
    // first contour stroke as well.
    const onAnnotationAdded = (evt: any) => {
      // Hard gate: if no annotation form is open, the user is not drawing —
      // ignore restored/programmatic adds so the "Add Annotation" button
      // doesn't get stuck disabled by background activity.
      if (!isSegmentationActiveRef.current) return;
      const ann = evt.detail?.annotation;
      if (!ann || ann.metadata?.toolName !== "PlanarFreehandContourSegmentationTool") return;
      // Skip programmatic loads (niftiMaskLoader on case open) and
      // region-grow propagation — only user-driven keyframes (lasso strokes,
      // region-grow seed clicks) should flip drawing state.
      if (isLoadedFromMask(ann) || ann.autoGenerated) return;
      const labelmapId = segmentationIdRef.current;
      if (!labelmapId) return;
      const contourId = getContourSegmentationId(labelmapId);
      const annSegId = ann.data?.segmentation?.segmentationId;
      if (annSegId !== labelmapId && annSegId !== contourId) return;
      setIsDrawing(true);
      setAnnotationStartTs((prev: string | null) => prev ?? new Date().toISOString());

      // 3D-viewer refresh + cross-plane jump fire on ANNOTATION_COMPLETED
      // (below), not here. ANNOTATION_ADDED fires at mousedown when the
      // polyline still has 0–1 points, so bumping here would re-derive
      // polylines before the stroke has geometry — and the NEXT bump
      // wouldn't fire until the user begins another stroke (one-stroke
      // render lag).
    };
    eventTarget.addEventListener("CORNERSTONE_TOOLS_ANNOTATION_ADDED" as any, onAnnotationAdded);

    // ANNOTATION_COMPLETED handler: bump contour version + jump companion
    // viewports to the stroke centroid. Same gating as the ADDED handler
    // above, but runs against the finalized polyline at mouseup.
    const onAnnotationCompleted = (evt: any) => {
      if (!isSegmentationActiveRef.current) return;
      const ann = evt.detail?.annotation;
      if (!ann || ann.metadata?.toolName !== "PlanarFreehandContourSegmentationTool") return;
      if (isLoadedFromMask(ann) || ann.autoGenerated) return;
      const labelmapId = segmentationIdRef.current;
      if (!labelmapId) return;
      const contourId = getContourSegmentationId(labelmapId);
      const annSegId = ann.data?.segmentation?.segmentationId;
      if (annSegId !== labelmapId && annSegId !== contourId) return;
      // Region-grow seeds carry `_regionGrow: true` (RegionGrowTool.ts:345).
      // Their cross-plane jump runs from setRegionGrowCommitCallback, so
      // skip the lasso/bump path here to avoid a duplicate jump.
      if ((ann as any)._regionGrow) return;

      // Register this lasso stroke with the annotation undo log so Ctrl+Z
      // removes the most-recent contour in correct order relative to bump
      // edits / deletions. removeAnnotation no-ops if it's already gone.
      //
      // Removal is by annotationUID — deliberately slice-agnostic: newest-first
      // undo reverts THIS stroke even if the user has since scrolled to a
      // different slice. This is the intended UX change from the pre-PR reactive
      // last-contour fallback (still slice-scoped, in useViewerHotkeys `mod+z`),
      // which only runs now once this log drains.
      const lassoUID = ann.annotationUID;
      // Existing-contour edits own a geometry checkpoint in SegmentationLassoTool.
      // Endpoint edits can emit COMPLETED, but must not also queue deletion.
      if (!isLassoContourEdit(ann))
        pushUndo({
          kind: "removeContour",
          undo: () => {
            try {
              getCornerstoneTools().annotation.state.removeAnnotation(lassoUID);
            } catch (err) {
              logger.warn("[SessionVolumeMount] lasso undo remove failed:", err);
            }
          },
        });

      const refImageId: string | undefined = ann.metadata?.referencedImageId;
      if (!refImageId) return;

      // Refresh the 3D viewer + stats overlay. SegmentationViewer3D re-derives
      // polylines whenever contourVersion bumps; without this, lasso strokes
      // wouldn't appear in 3D until the user saved or deleted something.
      setContourVersion((v) => v + 1);

      // Cross-plane companion-viewport sync. Mirrors the region-grow commit
      // callback: jump companions to the stroke centroid so the
      // sagittal/coronal views land on the lesion. Skip companions that
      // already have a committed contour for this segment, so multi-slice
      // editing doesn't yank the camera away from the user's current focus.
      // RELOCATED UNCHANGED (A4-note-3 owns any fix to this jump).
      try {
        const polyline: number[][] = ann.data?.contour?.polyline ?? [];
        if (polyline.length === 0) return;
        let cx = 0,
          cy = 0,
          cz = 0;
        for (const p of polyline) {
          cx += p[0];
          cy += p[1];
          cz += p[2];
        }
        const centroid: [number, number, number] = [
          cx / polyline.length,
          cy / polyline.length,
          cz / polyline.length,
        ];
        const cs = getCornerstone();
        const ct = getCornerstoneTools();
        const allAnns: any[] = ct.annotation.state.getAllAnnotations() ?? [];
        const engines = cs.getRenderingEngines?.() ?? [];

        // Find the "primary" viewport — the one whose imageIds contain the
        // annotation's referencedImageId (this is the viewport the user drew
        // on, not necessarily the viewport whose listener fired). Skip it
        // when jumping; the user just clicked it, so re-jumping would yank
        // the camera off the slice they're working on.
        let primaryVp: any = null;
        for (const engine of engines) {
          for (const vp of engine.getViewports?.() ?? []) {
            const ids: string[] = (vp as any).getImageIds?.() ?? [];
            if (ids.includes(refImageId)) {
              primaryVp = vp;
              break;
            }
          }
          if (primaryVp) break;
        }

        for (const engine of engines) {
          for (const vp of engine.getViewports?.() ?? []) {
            if (vp === primaryVp) continue;
            if (typeof (vp as any).jumpToWorld !== "function") continue;
            const vpImageIds: string[] = (vp as any).getImageIds?.() ?? [];
            const vpImageIdSet = new Set(vpImageIds);
            const hasOtherContour = allAnns.some(
              (a: any) =>
                a?.metadata?.toolName === "PlanarFreehandContourSegmentationTool" &&
                a?.data?.segmentation?.segmentIndex === activeSegmentIndex &&
                a?.metadata?.referencedImageId &&
                vpImageIdSet.has(a.metadata.referencedImageId) &&
                a?.annotationUID !== ann.annotationUID,
            );
            if (hasOtherContour) continue;
            try {
              (vp as any).jumpToWorld(centroid);
            } catch (err) {
              logger.debug("[SessionVolumeMount] lasso jumpToWorld skipped:", err);
            }
          }
        }
      } catch (err) {
        logger.debug("[SessionVolumeMount] lasso cross-plane nav failed:", err);
      }
    };
    eventTarget.addEventListener(
      "CORNERSTONE_TOOLS_ANNOTATION_COMPLETED" as any,
      onAnnotationCompleted,
    );

    // #725: editing an EXISTING contour (lasso handle-drag or bump-tool edit)
    // fires ANNOTATION_MODIFIED, not ANNOTATION_COMPLETED — so the 3D viewer
    // (SegmentationViewer3D re-derives on contourVersion) never refreshed after
    // an edit. Bump contourVersion on MODIFIED too, debounced because MODIFIED
    // fires continuously during a drag (a per-event bump would rebuild the mesh
    // every frame). Same gating as onAnnotationCompleted: our seg, a real
    // user-edit (not a loaded-mask restore or an auto-generated/interp contour).
    const onAnnotationModified = (evt: any) => {
      if (!isSegmentationActiveRef.current) return;
      const ann = evt.detail?.annotation;
      if (!ann || ann.metadata?.toolName !== "PlanarFreehandContourSegmentationTool") return;
      if (isLoadedFromMask(ann) || ann.autoGenerated) return;
      const labelmapId = segmentationIdRef.current;
      if (!labelmapId) return;
      const contourId = getContourSegmentationId(labelmapId);
      const annSegId = ann.data?.segmentation?.segmentationId;
      if (annSegId !== labelmapId && annSegId !== contourId) return;
      if (contourEditDebounce) clearTimeout(contourEditDebounce);
      contourEditDebounce = setTimeout(() => {
        setContourVersion((v) => v + 1);
      }, 250);
    };
    eventTarget.addEventListener(
      "CORNERSTONE_TOOLS_ANNOTATION_MODIFIED" as any,
      onAnnotationModified,
    );

    // Cross-viewport sync on annotation click. Cornerstone fires
    // ANNOTATION_SELECTION_CHANGE when the user clicks a contour in any
    // viewport; we mirror that into a jumpToWorld on every volume viewport so
    // the companion planes snap to the slice containing the lesion's centroid.
    const onAnnotationSelectionChange = (evt: any) => {
      const added: string[] = evt.detail?.added ?? [];
      if (added.length === 0) return;
      const cornerstone = getCornerstone();
      const cornerstoneTools = getCornerstoneTools();
      const ann = cornerstoneTools.annotation.state.getAnnotation?.(added[0]);
      const polyline: number[][] = ann?.data?.contour?.polyline ?? [];
      if (polyline.length === 0) return;
      let cx = 0,
        cy = 0,
        cz = 0;
      for (const p of polyline) {
        cx += p[0];
        cy += p[1];
        cz += p[2];
      }
      const centroid: [number, number, number] = [
        cx / polyline.length,
        cy / polyline.length,
        cz / polyline.length,
      ];
      // Identify the "primary" viewport — the one whose imageIds include the
      // selected annotation's referencedImageId. We must NOT re-jump it: the
      // user just placed/clicked the seed, so it's already at the right slice,
      // and under progressive HTJ2K the camera focal Z drifts from the
      // displayed slice index. Re-jumping moves the active axial view off
      // the stone the moment region-grow commits.
      const selectedRefImageId: string | undefined = ann?.metadata?.referencedImageId;
      const engines = cornerstone.getRenderingEngines?.() ?? [];
      let primaryVp: any = null;
      if (selectedRefImageId) {
        for (const engine of engines) {
          for (const vp of engine.getViewports?.() ?? []) {
            const ids: string[] = (vp as any).getImageIds?.() ?? [];
            if (ids.includes(selectedRefImageId)) {
              primaryVp = vp;
              break;
            }
          }
          if (primaryVp) break;
        }
      }
      for (const engine of engines) {
        for (const vp of engine.getViewports?.() ?? []) {
          if (vp === primaryVp) continue;
          if (typeof (vp as any).jumpToWorld === "function") {
            try {
              (vp as any).jumpToWorld(centroid);
            } catch (err) {
              logger.debug("[SessionVolumeMount] selection jumpToWorld skipped:", err);
            }
          }
        }
      }
    };
    eventTarget.addEventListener(
      "CORNERSTONE_TOOLS_ANNOTATION_SELECTION_CHANGE" as any,
      onAnnotationSelectionChange,
    );

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      if (contourEditDebounce) clearTimeout(contourEditDebounce);
      eventTarget.removeEventListener(
        "CORNERSTONE_TOOLS_SEGMENTATION_DATA_MODIFIED" as any,
        onSegmentationModified,
      );
      eventTarget.removeEventListener(
        "CORNERSTONE_TOOLS_ANNOTATION_ADDED" as any,
        onAnnotationAdded,
      );
      eventTarget.removeEventListener(
        "CORNERSTONE_TOOLS_ANNOTATION_COMPLETED" as any,
        onAnnotationCompleted,
      );
      eventTarget.removeEventListener(
        "CORNERSTONE_TOOLS_ANNOTATION_MODIFIED" as any,
        onAnnotationModified,
      );
      eventTarget.removeEventListener(
        "CORNERSTONE_TOOLS_ANNOTATION_SELECTION_CHANGE" as any,
        onAnnotationSelectionChange,
      );
    };
  }, [
    segReady,
    activeSegmentIndex,
    setIsDrawing,
    setAnnotationStartTs,
    setDerivedMeasurements,
    setContourVersion,
  ]);

  // ─── Wheel: page-scroll suppression ONLY (relocated from VolumeViewport
  //     2037-2051) ──────────────────────────────────────────────────────────
  // Slice STEPPING is owned by the shared tool group's AnatomicScroll tool
  // (bound to Wheel in configureToolGroup), which writes the slot sliceIndex
  // atom in list-index space (with listAnatomicSign) →
  // this mount's externalSliceIndex effect → session.setSlice, and the
  // VOLUME_NEW_IMAGE listener bridges the landed index to the readout. A manual
  // stepper here previously DOUBLE-STEPPED with AnatomicScroll (#7) — removed.
  // This guard only stops the page/container from scrolling while wheeling over
  // the viewport; { passive:false } is required for preventDefault(). It uses
  // stopPropagation (ancestors only — NOT stopImmediatePropagation), so
  // Cornerstone's same-element AnatomicScroll listener still fires.
  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };
    element.addEventListener("wheel", handleWheel, { passive: false });
    return () => element.removeEventListener("wheel", handleWheel);
  }, []);

  // ─── External slice control — navigate when the prop changes ──────────────
  useEffect(() => {
    if (externalSliceIndex === undefined) return;
    const session = sessionRef.current;
    if (!session) return;
    const next = clampIndex(externalSliceIndex, totalSlicesRef.current);
    if (next === currentSliceRef.current) return;
    currentSliceRef.current = next;
    session.setSlice(next);
    onSliceChangeRef.current?.(next, totalSlicesRef.current);
  }, [externalSliceIndex]);

  // ─── Window/Level preset apply (A4-next) — relocated from VolumeViewport
  //     1212-1250 ─────────────────────────────────────────────────────────────
  // Applies VOI when the windowCenter/Width props change (sidebar presets →
  // slot windowing atom). De-duped against lastAppliedWindowRef to break the
  // drag→VOI_MODIFIED→onWindowChange→atom→prop→here feedback loop. Gated on
  // toolsReady (flips only after attachStrategy resolved → volume actor exists).
  useEffect(() => {
    if (!toolsReady) return;
    const vpId = viewportIdRef.current;
    const volId = volumeIdRef.current;
    if (!vpId || !volId) return;
    if (windowCenter === undefined || windowWidth === undefined) return;
    const last = lastAppliedWindowRef.current;
    if (last && last.center === windowCenter && last.width === windowWidth) return;
    // 2nd arg: target volumeId on the volume path, undefined on the stack path
    // (where it would be `suppressEvents`). See the initial-VOI block.
    const volumeIdArg = viewportKindRef.current === "volume" ? volId : undefined;
    try {
      const vp = getOrCreateSharedRenderingEngine().getViewport(vpId) as unknown as {
        setProperties: (
          p: { voiRange?: { lower: number; upper: number } },
          volumeId?: string,
        ) => void;
        render: () => void;
      } | null;
      if (vp) {
        vp.setProperties(
          {
            voiRange: {
              lower: windowCenter - windowWidth / 2,
              upper: windowCenter + windowWidth / 2,
            },
          },
          volumeIdArg,
        );
        vp.render();
        lastAppliedWindowRef.current = { center: windowCenter, width: windowWidth };
      }
    } catch (e) {
      logger.debug("[SessionVolumeMount] preset VOI apply skipped:", e);
    }
  }, [windowCenter, windowWidth, toolsReady]);

  // ─── Invert apply (A4-next) — relocated from VolumeViewport 2016-2035 ────────
  useEffect(() => {
    if (isInverted === undefined || !toolsReady) return;
    const vpId = viewportIdRef.current;
    const volId = volumeIdRef.current;
    if (!vpId || !volId) return;
    const volumeIdArg = viewportKindRef.current === "volume" ? volId : undefined;
    try {
      const engine = getOrCreateSharedRenderingEngine();
      const vp = engine.getViewport(vpId) as unknown as {
        setProperties: (p: { invert?: boolean }, volumeId?: string) => void;
      } | null;
      if (vp) {
        vp.setProperties({ invert: isInverted }, volumeIdArg);
        engine.render();
      }
    } catch (e) {
      logger.debug("[SessionVolumeMount] invert apply skipped:", e);
    }
  }, [isInverted, toolsReady]);

  // ─── AI-jump cursor reset on series change (relocated from VV 1879-1881) ─────
  useEffect(() => {
    lastFiredAiJumpTriggerRef.current = null;
  }, [seriesId]);

  // ─── Cross-viewport AI jump (relocated from VolumeViewport 1882-1946) ────────
  // aiJumpTargetAtom carries the world-space IPP of the most recent AI-finding
  // click (CaseViewer/reporter). Every volume pane listens + snaps via the
  // strategy's jumpToWorld (orientation/order-agnostic). FoR authority is
  // canonical studyMetadata; the jump WORLD-POINT prefers Cornerstone's real
  // per-frame plane IPP over the target's sidecar ipp (#921 — the sidecar
  // value is synthesized on canonical studies and displaced on tilted series).
  useEffect(() => {
    if (!aiJumpTarget) return;
    if (lastFiredAiJumpTriggerRef.current === aiJumpTarget.trigger) return;

    const ownFor = studyMetadata?.series_metadata?.[seriesId]?.frame_of_reference_uid ?? null;
    const gate = checkAiJumpForGate(aiJumpTarget, seriesId, ownFor);
    if (!gate.ok) {
      logAiJumpBail(gate.reason, "effect", aiJumpTarget, seriesId, ownFor);
      lastFiredAiJumpTriggerRef.current = aiJumpTarget.trigger; // consume-on-bail
      return;
    }

    const session = sessionRef.current;
    // First-frame gate: strategy not attached yet → bail WITHOUT setting the
    // cursor so the mount-IIFE bind-site catches the target once attach resolves.
    if (!session) return;

    try {
      // Aim in the volume's REAL geometry frame — the sidecar ipp is
      // synthesized (z·normal) on canonical studies and lands slices away on
      // gantry-tilted series (#921). resolveAiJumpWorld prefers Cornerstone's
      // per-imageId plane IPP and warns on the sidecar fallback.
      const { world } = resolveAiJumpWorld(
        aiJumpTarget,
        getCornerstone().metaData,
        aiJumpImageIds(aiJumpTarget),
      );
      if (!session.showWorldNow(world)) {
        return; // no strategy yet → defer to bind-site, cursor unset
      }
      // Do NOT bridge aiJumpTarget.sliceIndex (it's the TARGET series' index,
      // not this viewport's landed index). jumpToWorld moves the camera → the
      // VOLUME_NEW_IMAGE listener resolves + writes the correct landed index.
      lastFiredAiJumpTriggerRef.current = aiJumpTarget.trigger;
      logger.info(
        "ai_slice_jump_cross seriesId=%s targetSeriesId=%s targetSliceIndex=%d trigger=%d",
        seriesId,
        aiJumpTarget.seriesId,
        aiJumpTarget.sliceIndex,
        aiJumpTarget.trigger,
      );
    } catch (err) {
      // Threw before navigating; leave the cursor untouched so the bind-site
      // sync applies this target when attach completes.
      logger.warn(
        "ai_slice_jump_cross jumpToWorld failed seriesId=%s targetSeriesId=%s err=%s",
        seriesId,
        aiJumpTarget.seriesId,
        err instanceof Error ? err.message : String(err),
      );
    }
  }, [aiJumpTarget, seriesId, studyMetadata]);

  // nnInteractive in-flight spinner (relocated from VolumeViewport 2069-2120).
  // The runtime sets the primary viewportId + any companion viewport ids so the
  // spinner shows on every viewport whose contours are pending. Without this the
  // new path gives no feedback during the segmentation call.
  const nnLoading = useAtomValue(nnInteractiveLoadingAtom);
  const thisViewportId = viewportIdRef.current ?? viewportIdOverride ?? null;
  const showNnSpinner =
    nnLoading.isLoading &&
    thisViewportId !== null &&
    (nnLoading.viewportId === thisViewportId ||
      (nnLoading.companionViewportIds ?? []).includes(thisViewportId));

  // Plane-color corner bracket (axial=blue, coronal=green, sagittal=red) —
  // relocated from VolumeViewport 2056-2067. At-a-glance orientation cue
  // matching the 3D viewer legend.
  const planeColor = useMemo(() => {
    // On a reslice (MPR companion) pane the rendered plane is the reslice
    // target, not the source series' native plane — so the corner bracket shows
    // the true orientation (coronal green / sagittal red), not the axial blue.
    const meta = studyMetadata?.series_metadata?.[seriesId];
    const plane = reslicePlane ?? (meta ? derivePlane(meta) : null);
    if (plane === "Axial") return PLANE_COLORS_HEX.axial;
    if (plane === "Coronal") return PLANE_COLORS_HEX.coronal;
    if (plane === "Sagittal") return PLANE_COLORS_HEX.sagittal;
    return null;
  }, [studyMetadata, seriesId, reslicePlane]);

  return (
    <div
      // `data-measure-target` mirrors VolumeViewport (2098) so over-viewport /
      // measure-pin hit-testing (CaseViewer.tsx target.closest) resolves the
      // new-path element too.
      data-measure-target="true"
      data-testid="session-volume-mount"
      ref={elementRef}
      className="relative w-full h-full bg-black"
      // Disable the browser's default context menu / image drag like the
      // Cornerstone element does; right-drag is a viewport gesture.
      onContextMenu={(e) => e.preventDefault()}
    >
      <SegmentationBadge activeTool={activeMeasurementTool} />
      {planeColor && (
        <div
          aria-hidden="true"
          className="absolute bottom-0 left-0 w-5 h-5 pointer-events-none"
          style={{
            borderLeft: `4px solid ${planeColor}`,
            borderBottom: `4px solid ${planeColor}`,
          }}
        />
      )}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center p-4 text-center text-sm text-red-300">
          {error}
        </div>
      )}
      {showNnSpinner && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/50 backdrop-blur-[1px] pointer-events-none"
          data-testid="nn-interactive-spinner"
        >
          <div className="h-10 w-10 rounded-full border-4 border-purple-400 border-t-transparent animate-spin" />
          <div className="text-sm text-white/90 font-medium tracking-wide">
            Running AI segmentation…
          </div>
        </div>
      )}
    </div>
  );
};

/** Clamp a list-index into [0, total-1]; mirrors NavigationController.clamp. */
function clampIndex(i: number, total: number): number {
  if (total <= 0) return 0;
  return Math.max(0, Math.min(total - 1, i));
}

export default SessionVolumeMount;
